// @ts-strict-ignore

/**
 * @module Conversations
 */

import { BehaviorSubject, Subject, Subscription, filter } from 'rxjs';
import { allSettled } from 'rainbow-web-sdk';

import { CallCapabilities, CallMedia, CallRB, CallStatus, CallType, CallRecordingState } from 'rainbow-web-sdk/models/call.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { BubbleRB, BubbleUser, BubbleUserStatus } from 'rainbow-web-sdk/models/bubble.model';
import { Mention, MentionLegacy, Message, MessageAdditionalContent, MessageID, MessageSide, MessageStatus, MessageType, Reaction, Urgency, formatMention } from 'rainbow-web-sdk/models/message.model';
import { GeoLoc } from 'rainbow-web-sdk/models/geoLoc.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { FileViewer } from 'rainbow-web-sdk/models/common/fileViewer';
import { Conversation, ConversationBookmark, ConversationEvents, ConversationMissedInfoCounters, ConversationRB, ConversationStatus, ConversationType } from 'rainbow-web-sdk/models/conversation.model';
import { FileDescriptor, FileState } from 'rainbow-web-sdk/models/common/fileDescriptor';
import { SearchTextConvResults, SearchTextMsgResult, SearchTextResult } from 'rainbow-web-sdk/models/searchTextConvResults.model';

import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService, ContactServiceEvents, ContactServiceInnerEvents } from 'rainbow-web-sdk/services/core/contact.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { I18nService } from 'rainbow-web-sdk/services/core/i18n.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { UrlMetaData } from 'rainbow-web-sdk/services/ulrMetadata/urlMetadata.service';
import { BotService } from 'rainbow-web-sdk/services/bot/bot.service';
import { Service } from 'rainbow-web-sdk/services/service';

import { BubbleServiceEvents, BubbleServiceInnerEvents, BubbleServiceRB, BUBBLE_SVC } from 'rainbow-web-sdk/services/bubbles/bubble.service';

import type { FileStorageService } from 'rainbow-web-sdk/services/files/fileStorage.service';
import { FILE_STORAGE_SVC } from 'rainbow-web-sdk/services/files/fileStorage.service';

import type { FileServerService } from 'rainbow-web-sdk/services/files/fileServer.service';
import { FILE_SERVER_SVC } from 'rainbow-web-sdk/services/files/fileServer.service';

import type { WebrtcGatewayService } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';
import { WEBRTC_GATEWAY_SVC } from 'rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service';

import type { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { TELEPHONY_SVC } from 'rainbow-web-sdk/services/telephony/telephony.service';

import type { BubbleConferenceServiceRB } from 'rainbow-web-sdk/services/conference/bubbleConference.service';
import { BUBBLE_CONFERENCE_SVC, BubbleConferenceServiceEvents } from 'rainbow-web-sdk/services/conference/bubbleConference.service';

import type { WebrtcP2PService } from 'rainbow-web-sdk/services/webrtc/webrtcP2P.service';
import { WEBRTC_P2P_SVC } from 'rainbow-web-sdk/services/webrtc/webrtcP2P.service';

import type { WebinarServiceRB } from 'rainbow-web-sdk/services/webinars/webinars.service';
import { WEBINAR_SVC } from 'rainbow-web-sdk/services/webinars/webinars.service';

import type { EmojiService } from 'rainbow-web-sdk/services/emoji.service';
import { EMOJI_SVC } from 'rainbow-web-sdk/services/emoji.service';

import type { NotificationCenterServiceRB } from 'rainbow-web-sdk/services/notifications/notificationCenter.service';
import { NOTIFCENTER_SVC } from 'rainbow-web-sdk/services/notifications/notificationCenter.service';

import { ConversationServiceEventHandler } from 'rainbow-web-sdk/services/conversation/conversationServiceEventHandler';
import { ConversationServiceBulkHistoryHandler } from 'rainbow-web-sdk/services/conversation/conversationServiceBulkHistoryHandler';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { Bubble, FileDocument, MENTION_REGEX, User } from 'rainbow-web-sdk/models';
import anonymizer from '../../utils/anonymizer';
import type { XMLElements } from '../../utils/xmlParser';
import { escape } from '../../libs';
import { $msg } from 'strophe.js';
import { TELEPHONY_CCD_AGENT_SVC, TelephonyCCDAgentService } from '../telephony/telephonyCcdAgent.service';
import { HORIZON_SVC } from '../horizon/horizon.service';
import { CITRIX_SVC } from '../citrix/citrix.service';
import { UserServiceRB } from '../users/user.service';

/** @internal */
export const CONVERSATION_SVC = 'ConversationService';

export interface ConversationService {

    /**
     * Subscribe to updates from the service (all events are of RBEvent);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     */
    subscribe(handler: (event: RBEvent<ConversationServiceEvents>) => any, eventNames?: ConversationServiceEvents | ConversationServiceEvents[]): Subscription;

    /**
     * Get the array of current conversations
     */
    getConversations(): Conversation[];

    /**
     * Get the array of all current call conversations, regardless of type (phone or webrtc)
     */
    getCallConversations(): Conversation[];

    /**
     * Async method which get conversation for a bubble, a user or via conversation id.
     * Conversation is first search in service cache then server side.
     * @param convRef - a bubble, a user or a conversation id
     * @param type - the conversation type
     */
    getConversation(convRef: string | User | Bubble | undefined, type?: 'contact' | 'room'): Promise<Conversation | undefined>;

    /**
     * This method remove locally and server side a conversation; Should be used on conversations already created on server and not local ones
     * @param conversation - the conversation to remove
     */
    removeConversation(conversation: Conversation): Promise<void>;

    /**
     * Method used to search for text in all rooms and conversation. The searched text length should be 3+ characters.
     * This API will return a list of all conversations / rooms with the number of occurances of the searched text in each one.
     * If you need to get the results for a given conversation/room only, then use this API: searchForTextInConversation
     * @param searchedText - the text to be searched in all conversations and rooms
     * @param limit - the maximum number results to take. Default value is 500;
     */
    globalSearch(searchedText: string, limit: number): Promise<SearchTextConvResults>;
}

export interface SendMessageData {

    /**
     * Additional content (like markdown or vcard) to be added to the message
     */
    additionalContent?: MessageAdditionalContent;

    /**
     * In case we want to reply to a given message
     */
    repliedMessage?: Message;

    /**
     * The list of mentions inside the message
     */
    mentions?: (Mention | MentionLegacy)[];

    /**
     * The urgency of the message; Allowed values are (standard) 'std', (notify) 'low', (warning) 'middle', (emergency) 'high'; Default value is "std"
     */
    urgency?: Urgency;

    /**
     * In case of urgency ACK message, the type of the ACK to send; Accepted values are "ack" or "ignore"
     */
    urgencyAckType?: 'ack' | 'ignore';

    /**
     * URL Meta data in case of sending URL
     */
    urlMetaData?: UrlMetaData;
}

export interface SendFileMessageData {
    /**
     * The file name to be shown in the conversation
     */
    fileName?: string;

    /**
     * The File we want to send (in case of upload from local device)
     */
    file?: File;

    /**
     * The FileDescriptor (in case of already existing file)
     * @internal
     */
    existingFile?: FileDescriptor;

    /**
     * The Document to send ( in case of already existing document)
     */
    document?: FileDocument;

    /**
     * In case of vocal message
     */
    voiceMessageData?: { duration: number; };

    /**
     * The urgency of the message; Allowed values are (standard) 'std', (notify) 'low', (warning) 'middle', (emergency) 'high'; Default value is "std"
     */
    urgency?: Urgency;
}

/**
 * @eventProperty
 */
export enum ConversationServiceEvents {

    /**
     * @eventProperty
     * This RBEvent is send when the conversation service is started and ready to be used (also fired after loss of network and reconnection)
     */
    ON_SERVICE_STARTED = 'ON_SERVICE_STARTED',

    /**
     * @eventProperty ON_CONVERSATION_CREATED
     * @param conversation - the new conversation
     * This RBEvent is send when a conversation has been created from the user (or from another client)
     */
    ON_CONVERSATION_CREATED = 'ON_CONVERSATION_CREATED',

    /**
     * @eventProperty ON_CONVERSATION_REMOVED
     * @param conversation - the concerned conversation
     * This RBEvent is send when a conversation has been removed from the user (or from another client)
     */
    ON_CONVERSATION_REMOVED = 'ON_CONVERSATION_REMOVED',

    /**
     * @eventProperty ON_MESSAGE_ADDED_IN_CONVERSATION
     * @param conversation - The conversation where the message is being added
     * This RBEvent is send when a new message is added to a conversation
     */
    ON_MESSAGE_ADDED_IN_CONVERSATION = 'ON_MESSAGE_ADDED_IN_CONVERSATION',

    /**
     * @eventProperty ON_MESSAGE_MODIFIED_IN_CONVERSATION
     * @param conversation - The conversation where the message is being modified
     * This RBEvent is send when a message is being modified (or deleted) in the conversation
     */
    ON_MESSAGE_MODIFIED_IN_CONVERSATION = 'ON_MESSAGE_MODIFIED_IN_CONVERSATION',

    /**
     * @eventProperty ON_NEW_CALL_IN_CONVERSATION
     * @param conversation - The conversation to which the call is attached
     * This RBEvent is send when a new call is added to the conversation; Could be used to manage the list of all conversations with calls
     */
    ON_NEW_CALL_IN_CONVERSATION = 'ON_NEW_CALL_IN_CONVERSATION',

    /**
     * @eventProperty ON_REMOVE_CALL_IN_CONVERSATION
     * @param  conversation - The conversation to which the call is removed
     * This RBEvent is send when a new call is removed from the conversation; Could be used to manage the list of all conversations with calls
     */
    ON_REMOVE_CALL_IN_CONVERSATION = 'ON_REMOVE_CALL_IN_CONVERSATION',

    /**
     * @eventProperty ON_CALL_IN_CONVERSATION_UPDATE
     * @param conversation - The conversation related to the call (sometimes the converation will be null, as it has been deleted)
     * @param call - The related call
     * This RBEvent is send when there's updated in the call inside the conversation; For Telephony calls, the conversation could be null;
     */
    ON_CALL_IN_CONVERSATION_UPDATE = 'ON_CALL_IN_CONVERSATION_UPDATE',

    /**
     * @eventProperty ON_ACTIVE_CALL_CHANGE
     * @param conversation - [optional] The conversation where the call is now ACTIVE  or RINGING_OUTGOING; COULD BE NULL if no call is active
     * @param call - [optional] The call hat is now ACTIVE or RINGING_OUTGOING; COULD BE NULL if no call is active (the current call is no longer active)
     * This RBEvent is send when there's update on the current active call; If no call is currently active, the event will be send with NO CONVERSATION AND NO CALL
     */
    ON_ACTIVE_CALL_CHANGE = 'ON_ACTIVE_CALL_CHANGE',
}

/**
 * @internal
 * Conversation service internal events send to other sevices via sendInnerEvent()
 * Should not be used by SDK user
 */
export enum ConversationServiceInnerEvents {
    /**
     * @eventProperty RAINBOW_ON_WEBCONF_ADD_HOC_COMPLETED
     * @param bubble - the bubble related to the conference
     * This RBEvent is send when adhoc conference starts in a new conversation; It's used to update the active conversation in the UI
     */
    RAINBOW_ON_WEBCONF_ADD_HOC_COMPLETED = 'RAINBOW_ON_WEBCONF_ADD_HOC_COMPLETED',

    /**
     * @eventProperty RAINBOW_ON_REPLACE_CONVERSATIONS_IN_EXTERNAL_POPOUT
     * @param conversation - the conversation related to the update
     * @param call - the call related to the update
     * This RBEvent is send in case of switch of conversation call (hybrid cases) and the external popup should update the UI accordingly
     */
    RAINBOW_ON_REPLACE_CONVERSATIONS_IN_EXTERNAL_POPOUT = 'RAINBOW_ON_REPLACE_CONVERSATIONS_IN_EXTERNAL_POPOUT',

    /**
     * @eventProperty RAINBOW_CONVERSATION_AUTO_ACK_CHECK
     * @param conversation - the conversation related
     * @param message - the related message
     * @param type - We only manage "IM" in case of IM message as the management is different;
     * @param checkIfChatIsHidden - in case of P2P or CONF cases only
     * This RBEvent is send in case we need if UCAAS should auto ack received message and decrease the missed counter;
     */
    RAINBOW_CONVERSATION_AUTO_ACK_CHECK = 'RAINBOW_CONVERSATION_AUTO_ACK_CHECK',

    /**
     * @eventProperty RAINBOW_CONVERSATION_CAPABILITIES_UPDATE
     * @param conversation - the conversation related
     * This RBEvent is send in case the capabilities for the conversation have changed
     */
    RAINBOW_CONVERSATION_CAPABILITIES_UPDATE = 'RAINBOW_CONVERSATION_CAPABILITIES_UPDATE',

    /**
     * @eventProperty RAINBOW_ORDER_CONVERSATIONS
     * This RBEvent is send in case the an order conversations
     */
    RAINBOW_ORDER_CONVERSATIONS = 'RAINBOW_ORDER_CONVERSATIONS'
}

/** @internal */
export class ConversationServiceRB extends Service implements ConversationService {

    public searchTextConvResults: SearchTextConvResults = new SearchTextConvResults();
    public conversationServiceBulkHistoryHandler: ConversationServiceBulkHistoryHandler | null = null;
    public readonly missedInfoCounters = new BehaviorSubject<ConversationMissedInfoCounters>(ConversationMissedInfoCounters.create());
    public pendingMessages: any = {};

    private conversations: any = {};
    private conversationCreationPromise: any = {};

    private readonly rxSubject = new Subject<RBEvent<ConversationServiceEvents>>();
    private readonly innerRxSubject = new Subject<RBEvent<ConversationServiceInnerEvents>>();

    private isBasicCallAllowed: boolean = false;
    private isSecondCallAllowed: boolean = false;
    private isSecondWebrtcCallAllowed: boolean = false;
    private isSecondWebrtcCallAllowedHybrid: boolean = false;
    private manageTwoWebrtcCallsAllowed: boolean = false;
    private activeCall: CallRB;

    private subscriptions: Subscription[] = [];
    private eventServiceSubscriptionHandlers: any[] = [];
    private xmppSubscriptionHandlers: any[] = [];

    private conversationServiceEventHandler: ConversationServiceEventHandler | null = null;

    // Mandatory services (always available)
    private mainService: MainService = MainService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private i18n: I18nService = I18nService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private readonly userServicec = UserServiceRB.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private botService: BotService = BotService.getInstance();

    // Injectable services, not always available
    private bubbleService?: BubbleServiceRB = undefined;
    private fileStorageService?: FileStorageService = undefined;
    private fileServerService?: FileServerService = undefined;
    private webConferenceService?: BubbleConferenceServiceRB = undefined;
    private webrtcP2PService?: WebrtcP2PService = undefined;
    private webinarService?: WebinarServiceRB = undefined;
    private telephonyService?: TelephonyService = undefined;
    private webrtcGatewayService?: WebrtcGatewayService = undefined;
    private notificationCenterService?: NotificationCenterServiceRB = undefined;
    private emojiService?: EmojiService = undefined;
    private reinitTimer: any = null;
    private reinitInProgress: Promise<any> = null;
    private ccdAgentService: TelephonyCCDAgentService = null;

    private horizonService;
    private citrixService;

    /** This static methods returns the conversationService singleton */
    public static getInstance(): ConversationServiceRB { return Service.create(CONVERSATION_SVC, ConversationServiceRB); }

    /** This static factory creates the conversationService singleton */
    public static build(): ConversationServiceRB { return new ConversationServiceRB(); }

    private constructor() { super(); }

    /* Inner events handlers */
    public subscribeToInnerEvents(handler: (event: RBEvent<ConversationServiceInnerEvents>) => any): Subscription { return this.innerRxSubject.subscribe(handler); }
    public sendInnerEvent(name: ConversationServiceInnerEvents, data: any = null): void { this.innerRxSubject.next(RBEvent.create(name, data)); }

    /** This async method has to be call to start the conversation service */
    public async start(): Promise<void> {
        try {
            this.logger.info('');
            this.logger.info('[conversationService] === STARTING ===');

            // Dynamically inject services (should not be here)
            this.bubbleService = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC);
            this.fileStorageService = this.mainService.inject<FileStorageService>(FILE_STORAGE_SVC);
            this.webConferenceService = this.mainService.inject<BubbleConferenceServiceRB>(BUBBLE_CONFERENCE_SVC);
            this.webrtcP2PService = this.mainService.inject<WebrtcP2PService>(WEBRTC_P2P_SVC);
            this.webinarService = this.mainService.inject<WebinarServiceRB>(WEBINAR_SVC);
            this.telephonyService = this.mainService.inject<TelephonyService>(TELEPHONY_SVC);
            this.fileServerService = this.mainService.inject<FileServerService>(FILE_SERVER_SVC);
            this.webrtcGatewayService = this.mainService.inject<WebrtcGatewayService>(WEBRTC_GATEWAY_SVC);
            this.emojiService = this.mainService.inject<EmojiService>(EMOJI_SVC);
            this.notificationCenterService = this.mainService.inject<NotificationCenterServiceRB>(NOTIFCENTER_SVC);
            this.ccdAgentService = this.mainService.inject<TelephonyCCDAgentService>(TELEPHONY_CCD_AGENT_SVC);

            //inject VDI services to disable some functionnalities
            this.horizonService = this.mainService.inject(HORIZON_SVC);
            this.citrixService = this.mainService.inject(CITRIX_SVC);

            // Allow the user to make PBX call on his deskphone using his Rainbow application
            this.isBasicCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_BASIC_CALL);

            // Allow to manage 2 PBX calls using the Rainbow application (Hold/retrieve/consult)
            this.isSecondCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_SECOND_CALL);
            this.isSecondWebrtcCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) || config.mixwebrtcpstn;
            this.isSecondWebrtcCallAllowedHybrid = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS_FOR_PBX) || config.mixwebrtcpstnhybride;
            this.manageTwoWebrtcCallsAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_MANAGE_TWO_CALLS) || config.mixwebrtcpstn;

            // Register to xmpp and call events
            this.attachHandlers();

            this.eventServiceSubscriptionHandlers.forEach(subscriptionHandler => subscriptionHandler());
            this.eventServiceSubscriptionHandlers = [];

            this.eventServiceSubscriptionHandlers.push(this.eventService.subscribe('ON_TELEPHONY_STATUS_CHANGED_EVENT', () => { this.onTelephonyStateChangeEvent(); }));
            this.eventServiceSubscriptionHandlers.push(this.eventService.subscribe('ON_WEBRTCGATEWAY_STATE_CHG', () => { this.onTelephonyStateChangeEvent(); }));
            this.eventServiceSubscriptionHandlers.push(this.eventService.subscribe('ON_CALL_UPDATED_EVENT', (__event: any, call: CallRB) => { this.onCallEvent(call); }));
            this.eventServiceSubscriptionHandlers.push(this.eventService.subscribe('ON_WEBRTC_GW_CALL_UPDATED_EVENT', (__event: any, call: CallRB) => { this.onCallEvent(call, true); }));
            // this.eventServiceSubscriptionHandlers.push(this.eventService.subscribe('ON_WEBRTCGATEWAY_STATE_CHG', () => { this.onTelephonyStateChangeEvent(); }));

            // Fetch conversations from server
            await this.getServerConversations();
            this.linkAllActiveCallsToConversations();
            this.computeCapabilitiesForMyContact();

            // Remove conversations that are forbidden according to customisation
            this.cleanUnwantedConversations();

            this.logger.info('[conversationService] === STARTED ===');

            if (this.bubbleService) {
                this.subscriptions.push(this.bubbleService.subscribeToInnerEvents((event: RBEvent) => {
                    try {
                        if (event.name === BubbleServiceInnerEvents.BUBBLE_ADMIN_MESSAGE_EVENT) {
                            this.onRoomAdminMessageEvent(event.data.roomJid, event.data.userJid, event.data.eventName, event.data.msgId, event.data.extraParameters);
                        }
                        else if (event.name === BubbleServiceInnerEvents.BUBBLE_HISTORY_READY) {
                            this.onRoomHistoryChangedEvent(event.data.room);
                        }
                    }
                    catch (error) {
                        this.logger.error(`[conversationService] roomService.subscribeToInnerEvents error -- ${error}`);
                    }
                }));
                this.subscriptions.push(this.bubbleService.subscribe((event: RBEvent) => {
                    try {
                        if (event.name === BubbleServiceEvents.ON_USER_BUBBLE_REMOVED) {
                            const bubbleUser: BubbleUser = event.data?.bubbleUser;

                            // Processes the event only if it concerns the connected user
                            if (bubbleUser.user.isConnectedUser()) {
                                const conversation = this.getConversationFromCache(event.data.bubble.jid);
                                if (conversation) this.removeConversation(conversation);
                            }
                        }
                    }
                    catch (error) {
                        this.logger.error(`[conversationService] roomService.subscribe error -- ${error}`);
                    }
                }));
            }

            this.subscriptions.push(this.contactService.subscribeToInnerEvents((event: RBEvent) => {
                try {
                    switch (event.name) {
                        case ContactServiceEvents.ON_CONNECTED_USER_PRESENCE_CHANGE: this.onMyContactChangedEvent(); break;
                        case ContactServiceEvents.ON_USER_PRESENCE_CHANGE: this.onContactChangedEvent(event.data); break;
                        case ContactServiceInnerEvents.ON_CONNECTED_USER_SETTINGS_UPDATED:
                            if (event.data === 'ringingOnDnd') this.onReloadCapabilitiesForMyContactEvent();
                            break;
                        default: break;
                    }
                }
                catch (error) {
                    this.logger.error(`[conversationService] contactService.subscribeToInnerEvents error -- ${error}`);
                }
            }));

            if (this.webConferenceService) {
                this.subscriptions.push(this.webConferenceService.eventSubscribe((event: RBEvent) => {
                    try {
                        if (event.name === BubbleConferenceServiceEvents.ON_CONFERENCE_STATUS_UPDATE) {
                            this.logger.debug('[conversationService] on ON_WEBCONFERENCE_STATUS_UPDATE -- should update conversation list');
                            // Update my contact capabilities
                            this.computeCapabilitiesForMyContact();
                        }
                    }
                    catch { /* empty */ }
                    return true;
                }));
            }

            this.updateMissedCounters();

            this.sendEvent(ConversationServiceEvents.ON_SERVICE_STARTED);
        }
        catch (error) {
            this.logger.error('[conversationService] === STARTING FAILURE === ' + error.message);
            throw error;
        }
    }

    public async reconnect(): Promise<void> {
        this.removeHandlers();
        this.attachHandlers();
    }

    private removeHandlers(): void {
        this.logger.info('[conversationService] removeHandlers ');

        // Remove conversation handlers
        this.conversationServiceEventHandler = null;
        this.conversationServiceBulkHistoryHandler = null;

        // Remove all xmpp handlers
        this.xmppSubscriptionHandlers.forEach((handler) => { this.xmppService.deleteHandler(handler); });
        this.xmppSubscriptionHandlers = [];
    }

    public attachHandlers() {

        this.logger.info('[conversationService] attachHandlers');

        this.conversationServiceEventHandler = ConversationServiceEventHandler.create();
        this.conversationServiceBulkHistoryHandler = ConversationServiceBulkHistoryHandler.create();

        this.xmppService.addRetrieveMessageHandler((stanza: any) => { return this.conversationServiceBulkHistoryHandler.onRetrieveMessageInConversationReceived(stanza); });
        this.xmppService.addBulkHistoryHandler((stanza: any) => { return this.conversationServiceBulkHistoryHandler.onHistoryMessageReceived(stanza); });

        // Attach messages handler (to detect some event even when user is not allowed to do any IM)
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onChatMessageReceived(stanza); return true; }, null, 'message', 'chat'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onErrorMessageReceived(stanza); return true; }, null, 'message', 'error'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onChatMessageReceived(stanza); return true; }, null, 'message', 'groupchat'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onFileMessageReceived(stanza); return true; }, null, 'message', 'file'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onWebRTCMessageReceived(stanza); return true; }, null, 'message', 'webrtc'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onPBXMessageReceived(stanza); return true; }, null, 'message', 'phone'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onManagementMessageReceived(stanza); return true; }, null, 'message', 'management'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onReceiptMessageReceived(stanza); return true; }, null, 'message'));
        this.xmppSubscriptionHandlers.push(this.xmppService.addHandler((stanza: any) => { this.conversationServiceEventHandler.onConferenceMessageReceived(stanza); return true; }, 'jabber:x:audioconference', 'message', 'chat'));
    }

    /**
     * This async method has to be call to stop the conversation service
     */
    public async stop(): Promise<void> {
        this.logger.info('[conversationService] === STOPPING ===');
        this.conversations = {};
        this.removeHandlers();

        // Remove all eventService handlers
        this.eventServiceSubscriptionHandlers.forEach(subscriptionHandler => subscriptionHandler());
        this.eventServiceSubscriptionHandlers = [];

        // Remove all subscriptions
        this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); });
        this.subscriptions = [];

        if (this.reinitTimer) {
            clearTimeout(this.reinitTimer);
            this.reinitTimer = null;
        }
        this.reinitInProgress = null;

        this.logger.info('[conversationService] === STOPPED ===');
    }

    public subscribe(handler: (event: RBEvent<ConversationServiceEvents>) => any, eventNames?: ConversationServiceEvents | ConversationServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<ConversationServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<ConversationServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[ConversationService] subscribe () -- error -- ${error.message}`); }
        });
    }

    /**
     * Contact service events send to other services via sendEvent()
     * Can listen to it via conversationService.subscribe() API
     * @param name - the name of the event
     * @param data -the data to be send (an object)
     */
    public sendEvent(name: ConversationServiceEvents, data: unknown = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    /**
     * Allows to send an event (of type RBEvent) via the RX subject of the instance, and to be captured by subscribe
     * @param rbEvent - event
     */
    public publish(rbEvent: RBEvent<ConversationServiceEvents, unknown>): void {
        this.rxSubject.next(rbEvent);
    }

    // ******************************************************************** //
    // ** CONVERSATION API STUFF                                         ** //
    // ******************************************************************** //

    private async promiseRacer(callback, timeout) {
        return Promise.race([
            callback,
            new Promise((__resolve, reject) => { setTimeout(() => { reject(new Error("getServerConversationsAndUpdate promiseRacer expired !")); }, timeout); })
        ]);
    }

    /**
     * This async method get valid conversations from the server and update the current list of conversations accordingly
     */
    private async getServerConversationsAndUpdate(): Promise<void> {
        this.logger.info('[conversationService] getServerConversationsAndUpdate');

        try {
            // Get and sort room data from server
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations?webinar=true`;
            const response = await fetch(url, { headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;

            const jsonData = await (this.promiseRacer(response.json(), 6000));
            const conversationsData = jsonData.data;

            conversationsData.forEach((conversationData: any) => {
                conversationData.creationDateSort = conversationData.creationDate ? new Date(conversationData.creationDate) : undefined;
                conversationData.lastModificationSort = conversationData.lastMessageDate ? new Date(conversationData.lastMessageDate) : undefined;
            });

            conversationsData.sort((aa: any, bb: any) => {
                const aDate = (!aa.lastModificationSort && aa.creationDateSort) ? aa.creationDateSort : aa.lastModificationSort;
                const bDate = (!bb.lastModificationSort && bb.creationDateSort) ? bb.creationDateSort : bb.lastModificationSort;
                return bDate - aDate;
            });

            //update the current conversations and remove obsolete ones
            Object.keys(this.conversations).forEach((key) => {
                const conversation: ConversationRB = this.conversations[key];
                const newConversationData = conversationsData.find((data: any) => data.jid_im === conversation.id);
                // Conversation does not exist anymore on server, remove it
                if (!newConversationData) {
                    //if the conversation is temporary and there's an active call on it, dont remove it
                    if (this.conversations[key]?.call?.callStatus && this.conversations[key]?.call?.callStatus !== CallStatus.ENDED && this.isTempTelephonyConversation(this.conversations[key].call)) {
                        //do not delete the conversation, as it's a temp PBX conversation
                        this.logger.info(`[conversationService] getServerConversationsAndUpdate -- do not remove conversation ${key} as there's a call inside ${this.conversations[key].call?.id}`);
                    }
                    else {
                        this.logger.info(`[conversationService] getServerConversationsAndUpdate -- remove conversation ${key}`);
                        this.conversations[key].reset();
                        delete this.conversations[key];
                    }
                }
                else {
                    this.logger.info(`[conversationService] getServerConversationsAndUpdate -- updateConversationFromData ${key}`);
                    this.updateConversationFromData(newConversationData);
                }
            });

            const conversationPromises = [];

            // Create new conversations
            conversationsData.forEach((conversationData: any) => {
                if (conversationData.type === 'room' && !this.bubbleService?.starting) {
                    this.logger.info('[conversationService] getServerConversationsAndUpdate -- create room conversation ' + conversationData.jid_im + ' ' + conversationData.id + ' -- ignored !');
                }
                else {
                    this.logger.info('[conversationService] getServerConversationsAndUpdate -- create conversation ' + conversationData.jid_im + ' ' + conversationData.id);
                    conversationPromises.push(this.createConversationFromData(conversationData));
                }
            });
            await allSettled(conversationPromises);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerConversationsAndUpdate');
            this.logger.error(`[conversationService] ${errorInfo.message}`);

            //add case for token expiration, go back to login page
            if (error?.errorDetailsCode === 401210 || error?.errorDetailsCode === 401202) {
                this.eventService.publish("ON_LOGOUT_EVENT", null, true);
            }

            throw errorInfo.error;
        }
    }

    /**
     * This async method permits to create a conversation from server data
     * @param conversationData - the conversation identifier
     */
    private updateConversationFromData(conversationData: ConversationServerData): ConversationRB {
        const missedImCounter = parseInt(conversationData.unreadMessageNumber, 10);
        const muted = (conversationData.mute);
        if (conversationData.type === 'user') {
            return this.updateOneToOneConversationInternal(conversationData.jid_im, conversationData.id, conversationData.lastMessageDate, conversationData.lastMessageText,
                missedImCounter, muted, conversationData.creationDate, conversationData.lastMessageSender, conversationData.call, conversationData.modified,
                conversationData.forwardFrom, conversationData.forwardedMessage, conversationData.filename, conversationData.geoloc, conversationData.contentType, conversationData.bookmark, conversationData);
        }

        return this.updateRoomConversationInternal(conversationData.jid_im, conversationData.id, conversationData.lastMessageDate, conversationData.lastMessageText,
            missedImCounter, muted, conversationData.creationDate, conversationData.lastMessageSender, conversationData.topic,
            conversationData.forwardFrom, conversationData.forwardedMessage, conversationData.eventName, conversationData.eventJid, conversationData.filename, false, conversationData.contentType, conversationData.bookmark, conversationData, conversationData.modified);
    }

    private updateOneToOneConversationInternal(conversationId: string, conversationDbId?: string, lastModification?: string, lastMessageText?: string,
        missedIMCounter?: number, muted?: boolean, creationDate?: string, lastMessageSenderJid?: string, lastCallInfo?: any, replace?: boolean,
        forwardFrom?: string, forwardedMessage?: string, filename?: string, isGeoLoc?: boolean, lastMessageContentType?: string, bookmark?: ConversationBookmark, conversationData?: any): ConversationRB {
        // Fetch existing conversation
        const conversation = this.getConversationFromCache(conversationId);

        if (conversation) {
            this.logger.info(`[conversationService] updateOneToOneConversationInternal ${conversationId} -- conversation found`);

            if (conversation.contact) {
                if (conversationData) {
                    if (conversationData.instantMessagesCustomisation) conversation.contact.instantMessagesEnabled = (conversationData.instantMessagesCustomisation !== 'disabled');
                    if (conversationData.useWebRTCAudioCustomisation) conversation.contact.webrtcAudioEnabled = (conversationData.useWebRTCAudioCustomisation !== 'disabled');
                    if (conversationData.useWebRTCVideoCustomisation) conversation.contact.webrtcVideoEnabled = (conversationData.useWebRTCVideoCustomisation !== 'disabled');
                    if (conversationData.useScreenSharingCustomisation) conversation.contact.webrtcSharingEnabled = (conversationData.useScreenSharingCustomisation !== 'disabled');
                    if (conversationData.readReceiptsCustomisation) conversation.contact.readReceiptsCustomisation = (conversationData.readReceiptsCustomisation !== 'disabled');
                    if (conversationData.useWebRTCOnlyIfMobileLoggedCustomisation) conversation.contact.useWebRTCOnlyIfMobileLogged = (conversationData.useWebRTCOnlyIfMobileLoggedCustomisation !== 'disabled');
                }
            }

            conversation.lastModification = lastModification ? new Date(lastModification) : undefined;
            conversation.lastCallInfo = lastCallInfo;
            conversation.lastMessage.messageId = replace ? "id" : "";
            conversation.lastMessage.text = lastMessageText;
            if (lastMessageContentType) { conversation.lastMessage.contentType = lastMessageContentType; }
            conversation.lastMessage.sender = this.xmppService.getBareJidFromJid(lastMessageSenderJid);
            conversation.muted = muted;
            conversation.creationDate = creationDate ? new Date(creationDate) : new Date();
            // this.computeCapabilitiesForContact(contact);
            conversation.dbId = conversationDbId;

            //if the user is not allowed to do IM, reset all missed im to 0
            if (!this.contactService.userContact?.instantMessagesEnabled) {
                missedIMCounter = 0;
            }
            conversation.setMissedCounter(missedIMCounter ? missedIMCounter : 0);

            if (forwardFrom && forwardFrom.indexOf("/") !== -1) {
                //it's a full jid, get only the jid of the user
                forwardFrom = this.xmppService.getBareJidFromJid(forwardFrom);
            }
            conversation.forwardFrom = forwardFrom;
            conversation.forwardedMessage = forwardedMessage;
            conversation.isForwardedMessageFile = Boolean(forwardFrom && filename);
            conversation.isGeoLoc = isGeoLoc;
            conversation.bookmark = bookmark ? bookmark : null;
            conversation.lastEvent = conversationData.event;
            conversation.sendEvent(ConversationEvents.ON_MESSAGES_UPDATED, undefined);
        }

        return conversation;
    }

    private updateRoomConversationInternal(roomJid: string, conversationDbId?: string, lastModification?: string, lastMessageText?: string, missedIMCounter?: number,
        muted?: any, creationDate?: any, lastMessageSenderJid?: string, description?: string, forwardFrom?: string, forwardedMessage?: string, eventName?: any, eventJid?: string, filename?: string, standard: boolean = true, lastMessageContentType?: string, bookmark?: ConversationBookmark, conversationData?: any, replace?: boolean): ConversationRB {

        const conversation = this.getConversationFromCache(roomJid);
        // ------------------------------------- //
        // -- ROOM FOUND                      -- //
        // ------------------------------------- //
        const room = this.bubbleService.getBubbleFromCache(roomJid);
        if (room) {
            this.logger.info(`[conversationService] updateRoomConversationInternal ${roomJid} ${standard ? '' : 'initMode'} -- room found`);

            conversation.dbId = conversationDbId;
            conversation.lastModification = lastModification ? new Date(lastModification) : undefined;
            if (lastMessageText) {
                if (eventName === 'conferenceRemove') { conversation.lastMessage.text = 'conferenceRemoveMsgRoom'; }
                else if (eventName === 'conferenceAdd') { conversation.lastMessage.text = 'conferenceAddMsgRoom'; }
                else if (eventName === 'voicemessage') { conversation.lastMessage.text = this.i18n.translate('newVoiceMessage'); conversation.lastMessage.side = MessageSide.ADMIN; }
                else {
                    conversation.lastMessage.messageId = replace ? "id" : "";
                    conversation.lastMessage.text = lastMessageText;
                    if (lastMessageContentType) { conversation.lastMessage.contentType = lastMessageContentType; }
                    conversation.lastMessage.sender = this.xmppService.getBareJidFromJid(lastMessageSenderJid);
                }
            }
            else {
                conversation.lastMessage.text = lastMessageText;
            }

            conversation.muted = muted;
            conversation.creationDate = creationDate ? new Date(creationDate) : new Date();
            if (eventName && eventJid) { conversation.lastMessage.sender = eventJid; }
            else { conversation.lastMessage.sender = this.xmppService.getBareJidFromJid(lastMessageSenderJid); }
            if (lastMessageContentType) { conversation.lastMessage.contentType = lastMessageContentType; }
            if (forwardFrom && forwardFrom.indexOf("/") !== -1) { forwardFrom = this.xmppService.getBareJidFromJid(forwardFrom); }
            conversation.forwardFrom = forwardFrom;
            conversation.forwardedMessage = forwardedMessage;
            conversation.isForwardedMessageFile = Boolean(forwardFrom && filename);

            if (conversation.bubble && description) { conversation.bubble.description = description; }

            //if the user is not allowed to do IM, reset all missed im to 0
            if (!this.contactService.userContact?.instantMessagesEnabled) {
                missedIMCounter = 0;
            }

            conversation.setMissedCounter(missedIMCounter ? missedIMCounter : 0);
            conversation.bookmark = bookmark ? bookmark : null;
            conversation.lastEvent = conversationData?.event;

            if (conversation.lastMessage?.text?.length > 20) { this.logger.info(`[conversationService] updateRoomConversationInternal ${roomJid} -- last message length ${conversation.lastMessage?.text?.length} -- ${conversation.lastMessage?.sender}`); }
            else { this.logger.info(`[conversationService] updateRoomConversationInternal ${roomJid} -- last message ${anonymizer(conversation.lastMessage?.text)} ${conversation.lastMessage?.sender}`); }

            conversation.sendEvent(ConversationEvents.ON_MESSAGES_UPDATED, undefined);
        }

        return conversation;
    }

    /**
     * This async method get valid conversations from the server
     */
    private async getServerConversations(): Promise<void> {

        this.logger.info('[conversationService] getServerConversations');

        try {
            // Get and sort room data from server
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations?webinar=true`;
            const response = await fetch(url, { headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            const conversationsData = (await response.json()).data;

            conversationsData.forEach((conversationData: any) => {
                conversationData.creationDateSort = conversationData.creationDate ? new Date(conversationData.creationDate) : undefined;
                conversationData.lastModificationSort = conversationData.lastMessageDate ? new Date(conversationData.lastMessageDate) : undefined;
            });

            conversationsData.sort((aa: any, bb: any) => {
                const aDate = (!aa.lastModificationSort && aa.creationDateSort) ? aa.creationDateSort : aa.lastModificationSort;
                const bDate = (!bb.lastModificationSort && bb.creationDateSort) ? bb.creationDateSort : bb.lastModificationSort;
                return bDate - aDate;
            });

            // Create conversations
            const maxConversations = config.sdk ? 3000 : 40;
            const conversationPromises = [];
            let nbValidConversations = 0;

            conversationsData.forEach((conversationData: any) => {
                const missedImCounter = parseInt(conversationData.unreadMessageNumber, 10);
                const isWebinar = conversationData.type === 'room' && ((conversationData.name as string).startsWith('Webinar 1 - ') || (conversationData.name as string).startsWith('Webinar 2 - '));
                const isInvalidConversation = nbValidConversations >= maxConversations && missedImCounter === 0;

                //MCO if (isInvalidConversation || isWebinar) { this.deleteServerConversation(conversationData.id); }
                if (isInvalidConversation) { this.deleteServerConversation(conversationData.id); }
                else if (conversationData.type === 'room' && (!this.bubbleService?.starting)) { }
                else {
                    conversationPromises.push(this.createConversationFromData(conversationData));
                    if (!isWebinar) nbValidConversations++;
                }
            });
            await allSettled(conversationPromises);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerConversations');
            this.logger.error(`[conversationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This async method create a conversation server side
     * @param conversation - the conversation to create server side
     */
    private async createServerConversation(conversation: ConversationRB): Promise<ConversationRB> {
        try {
            if (conversation.dbId) return conversation;
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations`;
            let body = null;
            switch (conversation.type) {
                case ConversationType.P2P: body = { peerId: conversation.contact.dbId, type: 'user' }; break;
                case ConversationType.BUBBLE: body = { peerId: conversation.bubble.dbId, type: 'room' }; break;
                case ConversationType.BOT:
                    conversation.type = ConversationType.P2P;
                    body = { peerId: conversation.contact.dbId, type: 'bot' };
                    break;
                default: break;
            }
            const response = await fetch(url, { method: 'POST', headers: this.authService.getPostHeader(), body: JSON.stringify(body) });
            if (!response.ok) throw response;
            const conversationData: ConversationServerData = (await response.json()).data;
            conversation.dbId = conversationData.id;

            //store the conversation, and then update it;
            if (!this.conversations[conversation.id]) { this.conversations[conversation.id] = conversation; }
            //make sure we use the same conversation
            conversation = this.conversations[conversation.id];

            //update conversation from server data;
            conversation = this.updateConversationFromData(conversationData);

            return conversation;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createServerConversation');
            this.logger.error(`[ConversationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This async method delete a conversation server side
     * @param conversationId - the identifier of the conversation to remove server side
     */
    private async deleteServerConversation(conversationId: string): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/${conversationId}`;
            const response = await fetch(url, { method: 'DELETE', headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;
            this.logger.info('[conversationService] deleteServerConversation success: ' + conversationId);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteServerConversation');
            // Check particular case where we are trying to remove an already removed conversation
            if (errorInfo.error.status === 404002) {
                this.logger.info('[conversationService] deleteServerConversation success: ' + conversationId);
                return;
            }
            this.logger.error(`[ConversationService] ${errorInfo.message}`);
            //throw errorInfo.error;
        }
    }

    /**
     * This async method update a conversation server side
     * @param conversation - the conversation to update server side
     */
    public async updateServerConversation(conversation: ConversationRB): Promise<ConversationRB> {
        // Ignore conversation without dbId
        if (!conversation.dbId) return conversation;

        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/${conversation.dbId}`;
            const response = await fetch(url, { method: 'PUT', headers: this.authService.getPostHeader(), body: JSON.stringify({ mute: conversation.muted }) });
            if (!response.ok) throw response;
            return conversation;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateServerConversation');
            this.logger.error(`[ConversationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This async method allow to ack all conversation message
     * @param conversation - the conversation
     * @param force - when true force the ack
     */
    public async ackAllMessages(conversation: ConversationRB, force: boolean = false): Promise<ConversationRB> {
        try {
            const result = conversation.ackReadAllReceivedMessages();
            if (!result && !force) {
                this.logger.info('[conversationService] ackAllMessages for conversation ' + conversation.dbId + ' already updated');
                return;
            }

            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/${conversation.dbId}/markallread`;
            const body = this.contactService.userContact.readReceiptsCustomisation ? {} : { maskRead: true };
            const response = await fetch(url, { method: 'PUT', headers: this.authService.getPostHeader(), body: JSON.stringify(body) });
            if (!response.ok) throw response;
            this.logger.info('[conversationService] ackAllMessages success: ' + conversation.dbId);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'ackAllMessages');
            this.logger.error(`[ConversationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method to set or replace a conversation bookmark; This API will provoque an update on the conversation object (event of type ConversationEvents.ON_MESSAGE_BOOKMARKED)
     * @param conversation - the conversation
     * @param message - the message to bookmark
     * @internal
     */
    public async setConversationBookmark(conversation: ConversationRB, message: Message): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/${conversation.dbId}/bookmark?analyticsConversationType=${conversation.type === ConversationType.P2P ? 'p2p' : 'bubble'}`;
            const body = { messageId: message.id };
            const response = await fetch(url, { method: 'POST', headers: this.authService.getPostHeader(), body: JSON.stringify(body) });
            if (!response.ok) throw response;
            this.logger.info('[conversationService] setConversationBookmark success: ' + conversation.dbId);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setConversationBookmark');
            this.logger.error(`[ConversationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method to set or delete a conversation bookmark
     * @param conversation - the conversation
     * @internal
     */
    public async deleteConversationBookmark(conversation: ConversationRB): Promise<void> {
        try {
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/${conversation.dbId}/bookmark?analyticsConversationType=${conversation.type === ConversationType.P2P ? 'p2p' : 'bubble'}`;
            const response = await fetch(url, { method: 'DELETE', headers: this.authService.getPostHeader() });
            if (!response.ok) throw response;
            this.logger.info('[conversationService] deleteConversationBookmark success: ' + conversation.dbId);

            //ACK all messages in the DB, to be sure that the counter will be updated
            this.ackAllMessages(conversation, true);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteConversationBookmark');
            this.logger.error(`[ConversationService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * This async method permits to get a conversation by conversationId
     * @param jid - the conversation identifier
     * @param type - the conversation type ('contact' or 'room') (optional)
     */
    public async getOrCreateConversation(jid: string, type?: string): Promise<ConversationRB> {
        const conversationType = type ? type : jid.startsWith('room') ? 'room' : 'contact';
        return this[conversationType === 'room' ? 'getRoomConversationInternal' : 'getOrCreateOneToOneConversationInternal'](jid);
    }

    /*
     * This async method permits to create a conversation from server data
     * @param conversationData - the conversation identifier
     */
    private async createConversationFromData(conversationData: ConversationServerData): Promise<ConversationRB> {
        if (conversationData.type === 'user') {
            return this.getOrCreateOneToOneConversationInternal(conversationData.jid_im, conversationData.id, conversationData);
        }
        else if (conversationData.type === 'bot') {
            //do sth
            return this.getOrCreateBotConversationInternal(conversationData.jid_im, conversationData.id, conversationData);
        }

        // We assume that this is a conversation for 1 bubble.
        return this.getRoomConversationInternal(conversationData.jid_im, conversationData.id, conversationData, false);
    }

    /**
     * This method create a local conversation object from a call
     * @param call - the call
     */
    public getOrCreateTelephonyConversation(call: CallRB): ConversationRB {
        this.logger.info(`getOrCreateTelephonyConversation -- ${call.id}`);
        let conv: ConversationRB = this.getConversationFromCache(call.id);

        //RQRAINB-10966++
        if (conv && conv?.contact !== call?.contact && (call.contact.o365Flag || call.contact.outlookFlag || call.contact.companyDirectoryFlag || call.contact.personalDirectoryFlag)) {
            conv.contact = call.contact; call.contact.conversation = conv;
            conv.sendEvent(ConversationEvents.ON_CONVERSATION_CALL_CONTACT_CHANGE, { call });
        } //RQRAINB-10966--

        if (!conv) {
            conv = new ConversationRB(call.id);
            if (call.isConference) { conv.telephonyContacts = call.participants; }
            else { conv.contact = call.contact; call.contact.conversation = conv; }
            this.conversations[call.id] = conv;
        }
        return conv;
    }

    /**
     * This method return the pre-existing conversation if any or create a new one
     * @param conversationId - the conversation identifier
     * @param conversationDbId - the conversation dbId
     * @param conversationData - the conversation data received from server
     */
    public getOrCreateOneToOneConversationInternal(conversationId: string, conversationDbId?: string, conversationData?: ConversationServerData): Promise<ConversationRB> {

        this.logger.info('[conversationService] getOrCreateOneToOneConversation ' + conversationId);
        if (!this.conversationCreationPromise[conversationId]) {

            // Fetch existing conversation
            const conv = this.getConversationFromCache(conversationId);
            if (conv) return Promise.resolve(conv);

            // No conversation found, then create it
            this.conversationCreationPromise[conversationId] = new Promise<ConversationRB>((resolve: any, reject: any) => {
                // Get userContact
                this.contactService.getOrCreateContact(conversationId)
                    .then((contact: Contact) => {
                        const conversation = ConversationRB.createConversation(contact);

                        this.computeCapabilitiesForContact(contact);

                        //the conversation already exists on server, avoid calling API
                        if (conversationDbId) {
                            conversation.dbId = conversationDbId;
                        }

                        // Create server conversation if needed
                        return this.createServerConversation(conversation);
                    })
                    .then((conversation: ConversationRB) => {
                        // Store conversation
                        if (!this.conversations[conversation.id]) { this.conversations[conversation.id] = conversation; }

                        //make sure we use the same conversation
                        conversation = this.conversations[conversation.id];

                        // If data is available, update the conversation
                        if (conversationData) this.updateConversationFromData(conversationData);

                        this.sendEvent(ConversationServiceEvents.ON_CONVERSATION_CREATED, { conversation });
                        resolve(conversation);
                        delete this.conversationCreationPromise[conversationId];
                    })
                    .catch((error) => {
                        const errorMessage = 'getOrCreateOneToOneConversation ' + conversationId + ' failure ' + error.message;
                        this.logger.error('[conversationService] ' + errorMessage);
                        reject(new Error(errorMessage));
                        delete this.conversationCreationPromise[conversationId];
                    });
            });
        }
        return this.conversationCreationPromise[conversationId];
    }

    /**
     * This method return the new bot conversation
     * @param conversationId - the conversation identifier
     * @param conversationDbId - the conversation dbId
     * @param conversationData - the conversation data received from server
     */
    public async getOrCreateBotConversationInternal(conversationId?: string, conversationDbId?: string, conversationData?: ConversationServerData) {
        try {
            // Fetch existing conversation
            const conv = this.getConversationFromCache(conversationId);
            if (conv) return Promise.resolve(conv);

            const botContact: Contact = await this.botService.getBotContactByJid(conversationId);
            if (botContact) {
                return this.getOrCreateOneToOneConversationInternal(conversationId, conversationDbId, conversationData);
            }

            throw new Error(`No contact found for bot ${conversationId} at all`);
        }
        catch (error) {
            const errorMessage = `getOrCreateBotConversationInternal failure: ${error.message} `;
            this.logger.error("[conversationService] " + errorMessage);
            throw new Error(errorMessage);
        }
    }

    /**
     * This method return the pre existing room converstion if any or create a new one
     * @param bubbleId - the bubble identifier, the id or the jid
     * @param conversationDbId - the conversation dbId
     * @param conversationData - the conversation data received from server
     * @param standard - if we should get all users inside the room, or only the strict minimum. Default value is TRUE
     */
    public async getRoomConversationInternal(bubbleId: string, conversationDbId?: string, conversationData?: ConversationServerData, standard: boolean = true): Promise<ConversationRB | undefined> {

        // First, find the bubble linked to the conversation either in the cache (By id or Jid) or from server via API request
        let adhocCase: boolean = false;
        let bubble: BubbleRB = this.bubbleService.getBubbleFromCache(bubbleId) as BubbleRB;

        if (!bubble) {
            // Bubble not in cache, get it froml server and set 'adhocCase' for this purpose.
            // Room conversation created by server, get the room and create the conversation locally
            this.logger.info(`[conversationService] getRoomConversation ${bubbleId} ${standard ? '' : 'initMode'} -- room not found -- get from server`);
            bubble = await this.bubbleService.getBubble(bubbleId) as BubbleRB;
            adhocCase = true;
        }

        // From there, we will always use the bubble's JID to manage the associated conversation
        const roomJid: string = bubble.jid;
        this.logger.info(`[conversationService] getRoomConversationInternal Room identifier:  Param: ${bubbleId} -- Used: ${roomJid}`);

        if (!this.conversationCreationPromise[roomJid]) {
            this.conversationCreationPromise[roomJid] = new Promise<ConversationRB>((resolve: any, reject: any) => {

                // Timeout the promise content to descheduled this part of code and permit the storage of promise in array
                setTimeout(async () => {

                    // ---------------------------------------------------- //
                    // -- CONVERSATION FOUND -> RETURN IT                -- //
                    // ---------------------------------------------------- //

                    // Only bubble Jid must be used, never its Id !
                    const conversation = this.getConversationFromCache(roomJid);

                    if (conversation) {
                        this.logger.info(`[conversationService] getRoomConversation ${roomJid} ${standard ? '' : 'initMode'} -- conversation found`);
                        if (standard) this.bubbleService.getAllBubbleUsers(conversation.bubble);
                        resolve(conversation);
                        delete this.conversationCreationPromise[roomJid];
                        return;
                    }

                    // --------------------------------------------------- //
                    // -- NO EXISTING CONVERSATION -> CREATE IT         -- //
                    // --------------------------------------------------- //

                    try {
                        this.logger.info(`[conversationService] getRoomConversation ${roomJid} ${standard ? '' : 'initMode'} -- conversation not found`);

                        // Handle very special case of already loaded public bubble
                        if (!bubble.bubbleConnectedUser) {
                            this.logger.info(`[conversationService] getRoomConversation ${roomJid} -- no connectedUser (certainly a public bubble)`);
                            await this.bubbleService.updateRoomFromServer(bubble);
                        }

                        if (standard) this.bubbleService.getAllBubbleUsers(bubble);

                        // Create a Conversation internal instance
                        let conversation = ConversationRB.createConversation(bubble);

                        if (conversationDbId) conversation.dbId = conversationDbId;

                        // Create server conversation if needed
                        conversation = await this.createServerConversation(conversation);
                        conversation.bubble = this.bubbleService.getBubbleFromCache(roomJid) as BubbleRB;

                        //do not erase existing conversation if there
                        if (!this.conversations[conversation.id]) { this.conversations[conversation.id] = conversation; }

                        //make sure we use the same conversation
                        conversation = this.conversations[conversation.id];

                        // If data is available, update the conversation
                        if (conversationData) this.updateConversationFromData(conversationData);

                        this.sendEvent(ConversationServiceEvents.ON_CONVERSATION_CREATED, { conversation });

                        if (conversation.bubble && conversation.bubble.bubbleConnectedUser.status !== BubbleUserStatus.UNSUBSCRIBED && conversation.bubble.isActive && standard) { this.bubbleService.sendInitialRoomPresenceSync(conversation.bubble); }
                        delete this.conversationCreationPromise[roomJid];
                        resolve(conversation);

                        // Check if room has already a conference started and update the active conference if any (add-hoc case)
                        if (adhocCase && this.conversations[conversation.id] && conversation.bubble && conversation.bubble.isBubbleConferenceStarted()) {
                            //set the conversation as active
                            this.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_ON_WEBCONF_ADD_HOC_COMPLETED, conversation.bubble);
                        }
                    }
                    catch (error) {
                        const errorMessage = `getRoomConversation failure: ${error.message} `;
                        this.logger.error("[conversationService] " + errorMessage);
                        reject(new Error(errorMessage));
                        delete this.conversationCreationPromise[roomJid];
                    }

                }, 1);
            });
        }
        return this.conversationCreationPromise[roomJid];
    }

    /**
     * This method remove locally and server side a conversation; Should be used on conversations already created on server and not local ones;
     * @param conversation - the conversation to remove
     */
    public async removeConversation(conversation: Conversation): Promise<void> {
        const conv = conversation as ConversationRB;
        this.logger.info('[conversationService] removeConversation ' + conv.id);
        await this.deleteServerConversation(conv.dbId);
    }

    /**
     * This method remove locally a conversation but not from the server
     * @param  conversation - the conversation to remove
     */
    public removeLocalConversation(conversation: ConversationRB): void {
        this.logger.info('[conversationService] remove conversation ' + conversation.id);

        if (conversation.videoCall && conversation.videoCall.callStatus !== CallStatus.ENDED) {
            this.logger.info('[conversationService] Ignore conversation deletion message for conversation' + conversation.id);
            return;
        }

        if (conversation.call) {
            // If we've any call inside the conversation, send event
            this.sendEvent(ConversationServiceEvents.ON_REMOVE_CALL_IN_CONVERSATION, { conversation });
        }

        delete this.conversations[conversation.id];

        if (!conversation.webinar?.isGuest) {
            this.sendEvent(ConversationServiceEvents.ON_CONVERSATION_REMOVED, { conversation });
        }

        // To avoid leak
        if (conversation.contact) {
            conversation.contact.conversation = null;
            conversation.contact = null;
        }
    }

    // ******************************************************* //
    // **                  MESSAGES STUFF                   ** //
    // ******************************************************* //

    public sendMessage(conversation: ConversationRB, messageText: string = "", messageData?: SendMessageData): Message | undefined {

        const id = ConversationRB.getUniqueMessageId();

        // Sanitize the text to avoid sending ESCAPE characters that would hang the websocket connection
        messageText = messageText.replace(/\u001B/g, '');

        // Do not translate shortnames (:wink:) or emoticons (:D, ;)) to unicode if message is of type /code (starts with '/code')
        const unicodeData: string = messageText ? ((messageText.startsWith('/code') ? messageText : this.shortnameToUnicode(messageText))) : '';
        const answeredMsgId: string = messageData?.repliedMessage ? messageData.repliedMessage.id : null;
        const answeredMsgDate = messageData?.repliedMessage ? messageData.repliedMessage.date : null;
        const creationDate = new Date();
        const from = this.contactService.userContact;
        const urgencyAck = messageData?.repliedMessage ? messageData.repliedMessage.urgencyAck : null;

        // Create conversation message
        const message: Message = this.addChatMessage(conversation, from, creationDate, unicodeData, id, true, null, answeredMsgId, answeredMsgDate, messageData?.additionalContent, messageData?.mentions, urgencyAck, null);
        if (!message) { return undefined; }
        message.urgency = messageData?.urgency;
        message.urgencyAck = urgencyAck;
        message.urgencyAckType = messageData?.urgencyAckType;
        message.urlMetadata = messageData?.urlMetaData;
        this.pendingMessages[message.id] = { conversation, message };
        this.sendXmppMessage(conversation, message);

        if (messageData?.mentions?.length && !conversation.bubble?.isWebinarBubble()) {
            this.notificationCenterService?.mentionUsersInConversation(messageData.mentions, conversation, message);
        }
        return message;
    }

    public async sendFileMessage(conversation: ConversationRB, messageData: SendFileMessageData): Promise<Message> {
        try {
            if (!this.fileStorageService || !this.fileServerService) {
                this.logger.error(`[ConversationService] sendFileMessage -- fileStorageService not available -- IGNORED`);
                throw new Error("[ConversationService] sendFileMessage -- fileStorageService not available for user");
            }

            let message: Message;

            if (messageData.document || messageData.existingFile) {
                const fd = messageData.existingFile ? messageData.existingFile : (messageData.document as any).fileDescriptor;
                if (!messageData.fileName) { messageData.fileName = fd.fileName; }
                const viewerType = conversation.type === ConversationType.P2P ? 'user' : 'room';
                const viewerOrRoomId = conversation.type === ConversationType.P2P ? conversation.contact.dbId : conversation.bubble.dbId;

                if (fd.isRainbowStorage()) {
                    await this.fileStorageService.addFileViewer(fd.id, viewerOrRoomId, viewerType);

                    message = this.addFSMessage(conversation, fd.id, messageData.fileName, fd.state, null, messageData.urgency);
                    message.fileName = fd.fileName;
                    this.sendXmppMessage(conversation, message, fd);
                }
                else {
                    const fileDescriptor = await this.fileStorageService.shareExternalStorageFiles(fd.path, fd.fileName, fd.id, viewerOrRoomId, viewerType);
                    message = this.addFSMessage(conversation, fileDescriptor.id, messageData.fileName, fileDescriptor.state, null, messageData.urgency);
                    this.sendXmppMessage(conversation, message, fileDescriptor);
                }

                this.pendingMessages[message.id] = { conversation, message };

            }
            else if (messageData.file) {

                // Create the fileDescriptor
                const viewerData = conversation.type === ConversationType.P2P ? { viewerId: conversation.contact.dbId, type: 'user' } : { viewerId: conversation.bubble.dbId, type: 'room' };
                const viewers = FileViewer.createFromData([viewerData]);
                const fileDescriptor = await this.fileStorageService.createFileDescriptor(messageData.file, viewers, messageData.voiceMessageData ? messageData.voiceMessageData : false);
                fileDescriptor.fileToSend = messageData.file;
                if (fileDescriptor.isImage() && messageData.file["preview"]) { fileDescriptor.previewBlob = messageData.file["preview"]; }
                if (!messageData.fileName) { messageData.fileName = messageData.file.name; }

                message = this.addFSMessage(conversation, fileDescriptor.id, messageData.fileName, FileState.UPLOADING, null, messageData.urgency);

                this.pendingMessages[message.id] = { conversation, message };

                // Upload the file to the server
                try {
                    fileDescriptor.setState(FileState.UPLOADING);
                    await this.fileServerService.uploadAFileByChunk(fileDescriptor, messageData.file);
                    fileDescriptor.setState(FileState.UPLOADED);
                    fileDescriptor.fileToSend = null;
                    fileDescriptor.chunkPerformed = 0;
                    fileDescriptor.chunkTotalNumber = 0;
                }
                catch (error) {
                    this.logger.error('[conversationService] sendFSMessage -- uploadAFile error');

                    // delete file descriptor
                    this.fileStorageService.deleteFileDescriptor(fileDescriptor.id);

                    fileDescriptor.setState(FileState.UPLOADERROR);
                    message.receiptStatus = MessageStatus.ERROR;
                    message.fileErrorMsg = error.translatedMessage ? error.translatedMessage : this.i18n.translate("unableToShareFile");
                    message.sendUpdateEvent();
                    throw error;
                }
                this.sendXmppMessage(conversation, message, fileDescriptor);
            }

            return message;
        }
        catch (error) {
            let throwError = true;
            if (error instanceof DOMException) {
                if (error.code === DOMException.ABORT_ERR) {
                    throwError = false;
                }
            }

            if (throwError) {
                let msgKey = error.translatedMessage ? error.translatedMessage : this.i18n.translate("unableToShareFile");
                if (error.errorDetailsCode === 403153) { msgKey = this.i18n.translate('fileSharingNoMoreAllowedMsg'); }
                if (error.errorDetailsCode === 403029 || error?.error?.errorDetailsCode === 403029) { msgKey = this.i18n.translate('externalStorageUserNotConnected'); }
                if (error.errorDetailsCode === 409021) { msgKey = this.i18n.translate('externalStorageSharedNotAuthorized'); }
                if (error.errorDetailsCode === 409023 || error?.error?.errorDetailsCode === 409023) { msgKey = this.i18n.translate('externalStorageSharedDisabled'); }
                if (error.errorDetailsCode === 503005 || error?.error?.errorDetailsCode === 503005) { msgKey = this.i18n.translate('externalStorageCreateSpaceNotAllowed'); }

                if (error.errorDetailsCode === 403630) {
                    const consumption = this.fileStorageService?.getConsumptionData();
                    let popupTitle = "storageAlmostFull";
                    if (consumption.maxValue - consumption.currentValue <= 0) {
                        popupTitle = "storageFull";
                    }
                    this.mainService.showGlobalPopupMessage({
                        iconId: "warning",
                        popupTitle: popupTitle,
                        popupBody: "storageAlmostFullBody",
                        okLabel: "manageRainbowStorage",
                        okAction: () => {
                            this.eventService.publish('ON_OPEN_CLEAN_FILES_TAB');
                        },
                        okColor: "blueFill",
                        cancelLabel: "close"
                    });
                } else if (error.errorDetailsCode === 403153 && error.errorDetailsData?.feature === "shareFileCustomisation") {
                    // RQRAINB-11706
                    this.mainService.showGlobalPopupMessage({
                        popupTitle: 'fileTransfer',
                        popupBodyTranslated: this.i18n.translate('SorryYouCantShareFileInConv'),
                        okLabel: 'ok'
                    });
                }
                else {
                    this.mainService.showGlobalPopupMessage({
                        popupTitle: 'fileTransfer',
                        popupBodyTranslated: msgKey,
                        okLabel: 'ok'
                    });
                }

                this.logger.error(`[conversationService] sendFileMessage -- error ${error.message}`);
                if (error instanceof Error)
                    throw error;

                throw new Error(error);
            }

            return null;
        }
    }

    public async forwardMessage(conversation: ConversationRB, destConv: ConversationRB, forwardedMessage: Message, anonymous: boolean = true): Promise<Message | undefined> {

        // Manage fileDescriptor
        let fileDescriptorToSend = null;
        if (forwardedMessage.fileId) {
            const file = this.fileStorageService.getFileDescriptorById(forwardedMessage.fileId);
            if (file?.ownerId !== this.contactService.userContact.dbId) { fileDescriptorToSend = await this.fileStorageService.copyFileToUserCloudStorage(file.id); }
            else fileDescriptorToSend = file;
        }

        let newForwardedMsg: Message = null;
        if (fileDescriptorToSend) {
            await this.shareFileToViewer(destConv, fileDescriptorToSend);
            newForwardedMsg = Message.createFileSharingMessage(forwardedMessage.id,
                forwardedMessage.date, forwardedMessage.from, forwardedMessage.side, forwardedMessage.data,
                fileDescriptorToSend.id, forwardedMessage.fileName, null, forwardedMessage.geoloc);
        }
        else {
            newForwardedMsg = Message.createFromMsg(forwardedMessage);

            if (forwardedMessage.forwardedInfo) {
                newForwardedMsg.id = forwardedMessage.forwardedInfo.id;
                newForwardedMsg.from = await this.contactService.getContactByJid(forwardedMessage.forwardedInfo.from);
            }
        }

        if (conversation.type === ConversationType.P2P) newForwardedMsg.to = (conversation.contact.jid !== forwardedMessage.from.jid) ? conversation.contact : this.contactService.userContact;
        else newForwardedMsg.to = conversation.bubble;

        if (forwardedMessage.urlMetadata) newForwardedMsg.urlMetadata = forwardedMessage.urlMetadata;
        else if (forwardedMessage.forwardedMsg?.urlMetadata) newForwardedMsg.urlMetadata = forwardedMessage.forwardedMsg.urlMetadata;
        if (!anonymous) newForwardedMsg.showIdentity = true;

        const id = ConversationRB.getUniqueMessageId();
        const creationDate = new Date();
        const from = this.contactService.userContact;

        // Create conversation message
        const message: Message = this.addForwardChatMessage(destConv, from, creationDate, id, newForwardedMsg, conversation);
        if (!message) { return undefined; }
        this.pendingMessages[message.id] = { destConv, newForwardedMsg, message };

        // Send Message to Forward
        this.sendXmppMessage(destConv, message);
        return message;
    }

    public async addReactionToMessage(conversation: ConversationRB, message: Message, emojiReaction?: string): Promise<Message> {
        try {
            if (!message) {
                this.logger.info('[conversationService] addReactionToMessage - original message not found');
                return null;
            }
            conversation.ackReadMessages();

            if (!emojiReaction) {
                this.logger.info('[conversationService] addReactionToMessage - No emoji');
                return null;
            }

            // Retrieve existing reaction user made to message
            const usedEmojisList = message.getReactionsByUser(this.contactService.userContact?.jid);
            const existingEmojiInd = usedEmojisList.indexOf(emojiReaction);
            if (existingEmojiInd < 0) {
                this.logger.info(`[conversationService] addReactionToMessage - ${emojiReaction} added`);
                usedEmojisList.push(emojiReaction);
            } else {
                this.logger.info(`[conversationService] addReactionToMessage - ${emojiReaction} already used: Do nothing`);
                return null;
            }
            const emojiReactionsToUse = usedEmojisList.join('');

            // Create the xmpp message
            const id = message.id;
            const ts = message.date.getTime().toString();
            const to = conversation.type === ConversationType.P2P ? conversation.contact.jid : conversation.bubble.jid;
            const from = this.contactService.userContact.jid;
            const type = conversation.type === ConversationType.P2P ? 'chat' : 'groupchat';
            const xmppMessage = $msg({ from, to, type, xmlns: 'jabber:client' })
                .c('reaction', { xmlns: 'urn:xmpp:reaction', msg_id: id, msg_ts: ts + '000' }).t(emojiReactionsToUse).up();

            // Create and send message
            await this.xmppService.send(xmppMessage);

            const reaction = Reaction.create(from, emojiReactionsToUse);
            message.updateReaction(reaction);

            return message;
        } catch (error) {
            this.logger.error(`[conversationService] addReactionToMessage -- error -- ${error.message}`);
            throw error;
        }
    }

    public async removeReactionFromMessage(conversation: ConversationRB, message: Message, emojiReaction?: string): Promise<Message> {
        try {
            if (!message) {
                this.logger.info('[conversationService] removeReactionFromMessage - original message not found');
                return null;
            }
            conversation.ackReadMessages();

            if (!emojiReaction) {
                this.logger.info('[conversationService] removeReactionFromMessage - No emoji');
                return null;
            }

            // Retrieve existing reaction user made to message
            const usedEmojisList = message.getReactionsByUser(this.contactService.userContact?.jid);
            const existingEmojiInd = usedEmojisList.indexOf(emojiReaction);
            if (existingEmojiInd < 0) {
                this.logger.info(`[conversationService] removeReactionFromMessage - ${emojiReaction} not found: Do nothing`);
                return null;
            }
            usedEmojisList.splice(existingEmojiInd, 1);
            const emojiReactionsToUse = usedEmojisList.join('');

            // Create the xmpp message
            const id = message.id;
            const ts = message.date.getTime().toString();
            const to = conversation.type === ConversationType.P2P ? conversation.contact.jid : conversation.bubble.jid;
            const from = this.contactService.userContact.jid;
            const type = conversation.type === ConversationType.P2P ? 'chat' : 'groupchat';
            const xmppMessage = $msg({ from, to, type, xmlns: 'jabber:client' })
                .c('reaction', { xmlns: 'urn:xmpp:reaction', msg_id: id, msg_ts: ts + '000' }).t(emojiReactionsToUse).up();

            // Create and send message
            await this.xmppService.send(xmppMessage);

            const reaction = Reaction.create(from, emojiReactionsToUse);
            message.updateReaction(reaction);

            return message;
        } catch (error) {
            this.logger.error(`[conversationService] removeReactionFromMessage -- error -- ${error.message}`);
            throw error;
        }
    }

    public modifyMessage(conversation: ConversationRB, message: Message, newMessageText: string = "", messageData: SendMessageData = {}): Message {
        if (!message) {
            this.logger.info('[conversationService] modifyMessage - original message not found');
            return null;
        }
        if (message.from.jid !== this.contactService.userContact.jid) {
            this.logger.info('[conversationService] modifyMessage forbidden Action - only sent messages can be modified');
            return null;
        }

        conversation.ackReadMessages();

        // Create the xmpp message
        const id = message.id;
        const to = conversation.type === ConversationType.P2P ? conversation.contact.jid : conversation.bubble.jid;
        const type = conversation.type === ConversationType.P2P ? 'chat' : 'groupchat';
        const data = newMessageText ? newMessageText : '';
        const unicodeData = this.shortnameToUnicode(data);
        const lang = this.contactService.currentLanguage;
        const xmppMessage = $msg({ to, type, id, 'xml:lang': lang })
            .c('body', { 'xml:lang': this.contactService.currentLanguage }).t(unicodeData).up()
            .c(data.length === 0 && (messageData.additionalContent === undefined || messageData.additionalContent === null) ? 'deleted' : 'modified', { xmlns: 'urn:xmpp:message-correct:0' }).up();

        // Handle alternative content
        if (messageData.additionalContent) {
            xmppMessage.c('content', { xmlns: ConversationRB.contentNS, type: messageData.additionalContent.type }).t(messageData.additionalContent.message).up();
        }

        // In case of modification of message, add also the answered message if any
        // But dont add in case of delete message
        if (message.answeredMsgId && (data.length !== 0 || (messageData.additionalContent !== undefined && messageData.additionalContent !== null))) {
            // Handle urgency acknowledgement
            if (message.urgencyAck && message.urgencyAckType) {
                xmppMessage.c('answeredMsg', {
                    type: 'urgent',
                    action: message.urgencyAckType,
                    stamp: message.answeredMsgDate.getTime().toString()
                }).t(message.answeredMsgId).up();
            } else {
                xmppMessage.c('answeredMsg', { stamp: message.answeredMsgDate.getTime().toString() }).t(message.answeredMsgId).up();
            }
        }

        //we need to compare the list of mentions before and after the update, and call the APIs accordingly
        if (!conversation.bubble?.isWebinarBubble() && this.notificationCenterService && (message.mentions.length || messageData.mentions?.length)) {
            //we need to update the text of the message before making the update of the mentions
            message.data = unicodeData;
            this.notificationCenterService?.updateMentionedUserInConversation(conversation, message, message.mentions, messageData.mentions);
        }

        // Handle mention in replace
        if (messageData.mentions?.length) {
            xmppMessage.c('mention', { xmlns: ConversationRB.AttentionNS });
            for (const mention of messageData.mentions) {
                // Search the contact model from its dbId
                const contact = this.contactService.getContactFromCacheById(mention.dbId);
                if (!contact) continue;

                // If found, append its jid to the mentions list
                if (mention instanceof Mention) {
                    xmppMessage.c('jid', {}, contact.jid);
                } else {
                    xmppMessage.c('jid', { pos: mention.position.toString(), size: mention.size.toString() }, contact.jid);
                }
            }
        }

        // Handle urgency in replace
        if (messageData.urgency) message.urgency = messageData.urgency;
        if (message.urgency && message.urgency !== 'std') {
            xmppMessage.c('headers', { xmlns: 'http://jabber.org/protocol/shim' }).c('header', { name: 'Urgency' }).t(message.urgency).up().up();
            // Add <retry-push xmlns='urn:xmpp:hints'/> tag - as per RQRAINB-7233
            xmppMessage.c('retry-push', { xmlns: 'urn:xmpp:hints' }).up();
        }

        // Deletion case
        if (!newMessageText && !messageData) { message.fileId = message.fileName = null; }

        xmppMessage.c('store', { xmlns: 'urn:xmpp:hints' }).up()
            .c('request', { xmlns: ConversationRB.ReceiptNS }).up()
            .c('active', { xmlns: ConversationRB.ChatstatesNS }).up();

        // Handle Url metadata
        const metadata = messageData.urlMetaData;
        if (metadata) {
            xmppMessage.c('url-metadata', { xmlns: 'urn:xmpp:message-urlmetadata', title: metadata.title, description: metadata.description, url: metadata.url, image: metadata.image, urlOnly: `${metadata.urlOnly}` }).up();
        }

        message.serverAckTimer = setTimeout(() => {
            message.receiptStatus = MessageStatus.ERROR; message.sendUpdateEvent();
        }, 10000);

        message.modify({ body: unicodeData, mentions: messageData.mentions, additionalContent: messageData.additionalContent });
        message.urlMetadata = metadata;

        // Create and send message
        this.xmppService.send(xmppMessage);

        // Update room lastActivityUpdate
        if (conversation.bubble) { conversation.bubble.lastActivityDate = new Date(); }

        conversation.updateAfterReplaceEvent(message);
        // this.orderConversations();

        this.pendingMessages[id] = { conversation, message: message };

        message.sendUpdateEvent();

        this.sendEvent(ConversationServiceEvents.ON_MESSAGE_MODIFIED_IN_CONVERSATION, { conversation, message });

        return message;
    }

    public deleteMessage(conversation: ConversationRB, message: Message): void {
        this.modifyMessage(conversation, message);
    }

    public async resendFSMessage(conversation: Conversation, message: Message): Promise<void> {
        try {
            if (!this.fileStorageService || !this.fileServerService) return;

            if (!message) return;

            // Get the original FileDescriptor previously created by sendFSMessage
            const fileDescriptor = this.fileStorageService.getFileDescriptorById(message.fileId);

            // Upload file
            try {
                this.pendingMessages[message.id] = { conversation, message };

                // Reinit idle state for File and message
                message.setReceiptStatus(MessageStatus.IN_PROGRESS);
                fileDescriptor.chunkPerformedPercent = 0;
                fileDescriptor.chunkPerformed = 0;
                fileDescriptor.chunkTotalNumber = 0;
                fileDescriptor.setState(FileState.UPLOADING);
                message.sendUpdateEvent();

                await this.fileServerService.uploadAFileByChunk(fileDescriptor, fileDescriptor.fileToSend);
                fileDescriptor.setState(FileState.UPLOADED);
                fileDescriptor.fileToSend = null;
                fileDescriptor.chunkPerformed = 0;
                fileDescriptor.chunkTotalNumber = 0;

                this.sendXmppMessage(conversation as ConversationRB, message, fileDescriptor);
            }
            catch (error) {
                this.logger.error('[conversationService] sendFSMessage -- uploadAFile error');

                // Do not delete file if it is attached
                //if (!attachedInfos) { this.fileStorageService.deleteFileDescriptor(fileDescriptor.id); }

                fileDescriptor.setState(FileState.UPLOADERROR);
                message.receiptStatus = MessageStatus.ERROR;
                message.fileErrorMsg = error.translatedMessage ? error.translatedMessage : this.i18n.translate("unableToShareFile");
                message.sendUpdateEvent();
                //this.sendXmppMessage(conversation, message, fileDescriptor);
                throw error;
            }
        }
        catch (error) {
            let msgKey = error.translatedMessage ? error.translatedMessage : this.i18n.translate("unableToShareFile");
            if (error.errorDetailsCode === 403153) { msgKey = this.i18n.translate('fileSharingNoMoreAllowedMsg'); }
            this.mainService.showGlobalPopupMessage({
                popupTitle: 'fileTransfer',
                popupBodyTranslated: msgKey,
                okLabel: 'ok'
            });
            throw new Error(error);
        }
    }

    /**
     * This methods shareFileToViewer
     * @param conversation - the conversation
     * @param fileDescriptor - the file descriptor
     */
    private async shareFileToViewer(conversation: ConversationRB, fileDescriptor: FileDescriptor): Promise<void> {
        if (!this.fileStorageService) {
            this.logger.error(`[ConversationService] shareFileToViewer -- fileStorageService not available -- IGNORED`);
            return;
        }

        if (!fileDescriptor) return;

        try {
            const viewerType = conversation.type === ConversationType.P2P ? 'user' : 'room';
            const viewerOrRoomId = conversation.type === ConversationType.P2P ? conversation.contact.dbId : conversation.bubble.dbId;
            await this.fileStorageService.addFileViewer(fileDescriptor.id, viewerOrRoomId, viewerType);
        }
        catch (error) {
            throw new Error(error);
        }
    }

    private sendXmppMessage(conversation: ConversationRB, message: Message, fileDescriptor: FileDescriptor = null): Message {
        if (!message) { return null; }

        const id: string = message.id;
        const unicodeData = (message.data.startsWith('/code')) ? message.data : this.shortnameToUnicode(message.data);
        const to: string = conversation.type === ConversationType.P2P ? conversation.contact.jid : conversation.bubble.jid;
        const type: string = conversation.type === ConversationType.P2P ? 'chat' : 'groupchat';

        // Create the xmpp message
        const xmppMessage = $msg({ to, type, id, 'xml:lang': this.contactService.currentLanguage });
        xmppMessage.c('body', { 'xml:lang': this.contactService.currentLanguage }).t(unicodeData).up();
        xmppMessage.c(message.noStore ? 'no-store' : 'store', { xmlns: 'urn:xmpp:hints' }).up();
        xmppMessage.c('request', { xmlns: ConversationRB.ReceiptNS }).up();
        xmppMessage.c('active', { xmlns: ConversationRB.ChatstatesNS }).up();

        // Handle fileDescriptor
        if (fileDescriptor) {

            xmppMessage.c('x', { xmlns: 'jabber:x:oob', index: message.attachIndex?.toString(), count: message.attachNumber?.toString() })
                .c('url', {}, config.restServerUrl + '/api/rainbow/fileserver/v1.0/files/' + fileDescriptor.id)
                .c('mime', {}, fileDescriptor.typeMIME)
                .c('filename', {}, fileDescriptor.fileName)
                .c('size', {}, fileDescriptor.size.toString()).up();
            message.fileId = fileDescriptor.id;

            // Handle voicemail
            if (fileDescriptor.tags && fileDescriptor.tags.purpose === 'voicemail') {
                xmppMessage.c('voicemessage', { xmlns: 'http://jabber.org/protocol/voicemessage' }).up();
            }
        }

        // Handle attached messageId
        if (message.attachedMsgId) {
            xmppMessage.c('attach-to', { xmlns: 'urn:xmpp:message-attaching:1', id: message.attachedMsgId }).up();
        }

        // Handle geoloc
        if (message.geoloc) {
            xmppMessage.c('geoloc', { xmlns: 'http://jabber.org/protocol/geoloc' })
                .c('lat', message.geoloc.lat).up()
                .c('lon', message.geoloc.lon).up().up();
        }

        // Handle alternative content
        if (message.additionalContent) {
            xmppMessage.c('content', { xmlns: ConversationRB.contentNS, type: message.additionalContent.type }).t(message.additionalContent.message).up();
        }

        // Handle answeredMessage
        if (message.answeredMsgId) {
            // Handle urgency acknowledgement
            if (message.urgencyAck && message.urgencyAckType) {
                xmppMessage.c('answeredMsg', {
                    type: 'urgent',
                    action: message.urgencyAckType,
                    stamp: message.answeredMsgDate.getTime().toString()
                }).t(message.answeredMsgId).up();
            } else {
                xmppMessage.c('answeredMsg', { stamp: message.answeredMsgDate.getTime().toString() }).t(message.answeredMsgId).up();
            }
        }

        // Handle mention
        if (message.mentions.length > 0) {
            xmppMessage.c('mention', { xmlns: ConversationRB.AttentionNS });
            for (const mention of message.mentions) {
                // Search the contact model from its dbId
                const contact = this.contactService.getContactFromCacheById(mention.dbId);
                if (!contact) continue;

                // If found, append its jid to the mentions list
                if (mention instanceof Mention) {
                    xmppMessage.c('jid', {}, contact.jid);
                } else {
                    xmppMessage.c('jid', { pos: mention.position.toString(), size: mention.size.toString() }, contact.jid);
                }
            }
            xmppMessage.up();
        }

        // Handle urgency
        if (message.urgency && message.urgency !== 'std') {
            xmppMessage.c('headers', { xmlns: 'http://jabber.org/protocol/shim' }).c('header', { name: 'Urgency' }).t(message.urgency).up().up();
            // Add <retry-push xmlns='urn:xmpp:hints'/> tag - as per RQRAINB-7233
            xmppMessage.c('retry-push', { xmlns: 'urn:xmpp:hints' }).up();
        }

        // Handle Forwarded Message
        if (message.forwardedMsg) {
            this.addXmppForwardMessage(xmppMessage, message);
        }

        // Handle Url metadata
        if (message.urlMetadata) {
            const metadata = message.urlMetadata;
            xmppMessage.c('url-metadata', { xmlns: 'urn:xmpp:message-urlmetadata', title: metadata.title, description: metadata.description, url: metadata.url, image: metadata.image, urlOnly: metadata.urlOnly.toString() }).up();
        }

        // Add message timeout
        message.serverAckTimer = setTimeout(() => { message.receiptStatus = MessageStatus.ERROR; message.sendUpdateEvent(); }, 10000);

        message.setReceiptStatus(MessageStatus.SENT);

        // Send the xmpp message
        this.xmppService.send(xmppMessage);

        // Update room lastActivityUpdate
        if (conversation.bubble) { conversation.bubble.lastActivityDate = new Date(); }

        //update the conversation status to active after sending a message
        conversation.status = ConversationStatus.ACTIVE;

        return message;
    }

    private addXmppForwardMessage(xmppMessage, message: Message) {

        xmppMessage.c('forwarded', { xmlns: ConversationRB.FORWARDNS });

        const unicodeData = this.shortnameToUnicode(message.forwardedMsg.data);
        const from = (message.forwardedMsg.from.fullJid) ? message.forwardedMsg.from.fullJid : message.forwardedMsg.from.jid;

        // GFR : to: message.forwardedMsg.to,  not possible missing information into Message
        xmppMessage.c('message', {
            to: message.forwardedMsg.to.jid, from: from, id: message.forwardedMsg.id,
            type: message.forwardConvType, xmlns: 'jabber:client'
        });
        xmppMessage.c('body', { 'xml:lang': this.contactService.currentLanguage }).t(unicodeData).up();

        // Handle fileDescriptor
        if (message.forwardedMsg.fileId && this.fileStorageService) {
            const fileDescriptor = this.fileStorageService.getFileDescriptorById(message.forwardedMsg.fileId);
            if (fileDescriptor) {
                xmppMessage.c('x', { xmlns: 'jabber:x:oob', index: message.attachIndex, count: message.attachNumber })
                    .c('url', {}, config.restServerUrl + '/api/rainbow/fileserver/v1.0/files/' + fileDescriptor.id)
                    .c('mime', {}, fileDescriptor.typeMIME)
                    .c('filename', {}, fileDescriptor.fileName)
                    .c('size', {}, fileDescriptor.size).up();

                // Handle voicemail
                if (fileDescriptor.tags && fileDescriptor.tags.purpose === 'voicemail') {
                    xmppMessage.c('voicemessage', { xmlns: 'http://jabber.org/protocol/voicemessage' }).up();
                }
            }
        }

        // Handle geoloc
        if (message.forwardedMsg.geoloc) {
            xmppMessage.c('geoloc', { xmlns: 'http://jabber.org/protocol/geoloc' })
                .c('lat', message.forwardedMsg.geoloc.lat).up()
                .c('lon', message.forwardedMsg.geoloc.lon).up().up();
        }

        // Handle answeredMessage
        if (message.answeredMsgId) {
            xmppMessage.c('answeredMsg', { stamp: message.forwardedMsg.answeredMsgDate.getTime() })
                .t(message.forwardedMsg.answeredMsgId).up();
        }

        // Handle Url metadata
        if (message.forwardedMsg.urlMetadata) {
            const metadata = message.forwardedMsg.urlMetadata;
            xmppMessage.c('url-metadata', { xmlns: 'urn:xmpp:message-urlmetadata', title: metadata.title, description: metadata.description, url: metadata.url, image: metadata.image }).up();
        }

        // Handle anonymous forward
        if (message.forwardedMsg.showIdentity) {
            xmppMessage.c('show-identity', { xmlns: 'urn:xmpp:hints' }).up();
        }

        xmppMessage.up(); // close Message
        xmppMessage.up(); // close forwarded
    }

    public sendAckReadOrReceivedMessage(conversation: ConversationRB, id: string, event: string) {
        this.logger.info(`[ConversationService] ${conversation.id} send ${event} status for message ${id}`);
        const to = (conversation.type === ConversationType.P2P) ? conversation.contact.jid : conversation.bubble.jid;
        const type = (conversation.type === ConversationType.P2P) ? 'chat' : 'groupchat';
        const from = this.contactService.userContact.jid;
        const maskRead = !this.contactService.userContact.readReceiptsCustomisation;
        const receivedAttr: any = { xmlns: ConversationRB.ReceiptNS, id, event, entity: 'client' };
        if (event === 'read' && conversation.type === ConversationType.P2P && maskRead) { receivedAttr.maskread = 'true'; }
        if (conversation.type !== ConversationType.P2P) { receivedAttr.type = 'muc'; }
        this.xmppService.send($msg({ to, from, type, id: "web_" + crypto.randomUUID() }).c('received', receivedAttr));
    }

    public sendAckReceivedMessage(conversation: ConversationRB, message: Message) {
        // No need to send RECV for messages we've already received or read
        if (message.receiptStatus >= MessageStatus.UNREAD) { return; }
        this.sendAckReadOrReceivedMessage(conversation, message.id, 'received');
        message.setReceiptStatus(MessageStatus.UNREAD);
    }

    public sendAckReadMessage(conversation: ConversationRB, message: Message): void {
        // No need to send ACK for messages we've already read
        if (message.receiptStatus === MessageStatus.READ) { return; }
        this.sendAckReadOrReceivedMessage(conversation, message.id, 'read');
        message.setReceiptStatus(MessageStatus.READ);

        // Ack all replaced/updated msg
        if (message.replaceMsgs?.length) {
            message.replaceMsgs.forEach((msg: Message) => { this.sendAckReadOrReceivedMessage(conversation, msg.id, 'read'); });
        }
    }

    public sendAckReadMessages(conversation: ConversationRB): boolean {
        this.logger.info(`[ConversationService] ${conversation.id} sendAckReadMessages`);
        let result = false;
        conversation.messages.forEach((message: Message) => {
            if ((message.receiptStatus === MessageStatus.SENT || message.receiptStatus === MessageStatus.UNREAD) && (message.side === MessageSide.LEFT || message.side === MessageSide.ADMIN)) {
                conversation.ackReadMessage(message);
                result = true;
            }
        });
        return result;
    }

    public sendChatStatus(conversation: ConversationRB, status: ConversationStatus) {
        if (conversation.status === status) { return; }
        this.logger.info(`[ConversationService] ${conversation.id} setStatus ${status} `);
        const id = ConversationRB.getUniqueMessageId();
        conversation.status = status;
        const to = (conversation.type === ConversationType.P2P) ? conversation.contact.jid : conversation.bubble.jid;
        const type = (conversation.type === ConversationType.P2P) ? 'chat' : 'groupchat';
        this.xmppService.send($msg({ to, type, id }).c(status, { xmlns: ConversationRB.ChatstatesNS }));
    }

    public sendIsTypingState(conversation: ConversationRB, isTypingState: boolean): void {
        //check the current status before sending a message
        if (conversation.status === ConversationStatus.ACTIVE && !isTypingState) return;
        if (conversation.status === ConversationStatus.COMPOSING && isTypingState) return;

        const id = ConversationRB.getUniqueMessageId();
        const state = (isTypingState) ? 'composing' : 'active';
        conversation.status = (isTypingState) ? ConversationStatus.COMPOSING : ConversationStatus.ACTIVE;
        const to = conversation.type === ConversationType.P2P ? conversation.contact.jid : conversation.bubble.jid;
        const type = conversation.type === ConversationType.P2P ? 'chat' : 'groupchat';
        const lang = this.contactService.currentLanguage;
        this.xmppService.send($msg({ to, type, id, 'xml:lang': lang }).c(state, { xmlns: ConversationRB.ChatstatesNS }).up());
    }

    public sendRecordingMessage(conversation: ConversationRB, data: string): Message {

        const id = ConversationRB.getUniqueMessageId();
        const to = conversation.contact.jid;
        const type = 'chat';
        const unicodeData = this.shortnameToUnicode(data);
        const lang = this.contactService.currentLanguage;

        // Create the message
        const message: Message = this.addRecordingMessage(conversation, this.contactService.userContact, new Date(), unicodeData, id);
        if (!message) { return null; }

        // Create the xmpp message
        const xmppMessage = $msg({ to, type, id, 'xml:lang': lang })
            .c('body', { 'xml:lang': lang }).up()
            .c('recording', { xmlns: 'jabber:iq:recordingP2P' }).t(unicodeData).up()
            .c('request', { xmlns: ConversationRB.ReceiptNS }).up()
            .c('active', { xmlns: ConversationRB.ChatstatesNS }).up();

        // Handle timeout
        message.serverAckTimer = setTimeout(() => { message.receiptStatus = MessageStatus.ERROR; message.sendUpdateEvent(); }, 10000);

        // Create and send message
        this.xmppService.send(xmppMessage);

        this.pendingMessages[message.id] = { conversation, message };
        return message;
    }

    /**
     * removeAllMessages
     * @param conversation - the conversation
     */
    public removeAllMessages(conversation: ConversationRB): Promise<void> {
        return new Promise<void>((resolve: any) => {
            this.logger.info('[conversationService] removeAllMessage ' + conversation.id);

            const mamRequestId = this.xmppService.connection.getUniqueId(null);
            const mamRequest = { queryid: mamRequestId, with: conversation.id, onComplete: () => { resolve(); } };
            const queryId = this.xmppService.connection.getUniqueId(null);

            // Request for history messages
            this.xmppService.connection.mam.delete(queryId, mamRequest);
        });
    }

    /**
     * This Methods allow to send the text content of a converstion to the connectedUser email address
     * @param conversation - the conversation
     */
    public async sendConversationByEmail(conversation: ConversationRB): Promise<void> {
        try {
            const userContact = this.contactService.userContact;
            const url = config.restServerUrl + '/api/rainbow/enduser/v1.0/users/' + userContact.dbId + '/conversations/' + conversation.dbId + '/downloads';
            const response = await fetch(url, { method: 'POST', headers: this.authService.getPostHeader() });
            if (!response.ok) throw response;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getServerConversations');
            if (errorInfo.error.status === 409011) {
                error.message = 'No messages to export for the last 30 days.';
                this.logger.info('[conversationService] deleteServerConversation success: ' + error.message);
                return;
            }
            this.logger.error(`[ConversationService] ${errorInfo.message} `);
            throw errorInfo.error;
        }
    }

    /**
     * This async method is used to retreive messages around a specified date (for search mechanism)
     * @param conversation - the conversation
     * @param msgDate - the centered date
     */
    public retrieveMsgByDate(conversation: ConversationRB, msgDate: any, messageId: MessageID): Promise<void> {
        // Sometimes the date is not in the correct format
        if (msgDate?.getTime) { msgDate = msgDate.getTime(); }
        this.logger.debug('[conversationService] >retrieveMsgByDate: ' + msgDate);
        //add 1 milisec to the search date
        msgDate = parseInt(msgDate, 10) + 1;
        return this.conversationServiceBulkHistoryHandler.retriveMessageInConversation(conversation, msgDate + '000', messageId);
    }

    // ************************************************ //
    // ** PUBLIC SEARCH FOR TEXT APIs                ** //
    // ************************************************ //

    /**
     * clear Search result
     */
    public clearSearchTextResults() {
        this.searchTextConvResults.clearAll();
        this.searchTextConvResults.isSearching = false;
    }

    public async globalSearch(searchedText: string = "", limit: number = 500): Promise<SearchTextConvResults> {
        if (this.searchTextConvResults.isSearching) { throw new Error("[conversationService] globalSearch -- search already in progress"); }
        if (searchedText.length < 3) {
            this.searchTextConvResults.totalCount = 0;
            this.searchTextConvResults.results = [];
            this.searchTextConvResults.searchedText = "";
            throw new Error("[conversationService] globalSearch -- min search length should be 3 characters");
        }

        try {
            // Check if same search is already done and avoid to do it again
            if (this.searchTextConvResults.searchedText && searchedText.indexOf(this.searchTextConvResults.searchedText) !== -1) {
                if (this.searchTextConvResults.totalCount === 0) {
                    //last search with same criteria is empty, return result directly
                    return this.searchTextConvResults;
                }
            }
            this.searchTextConvResults.isSearching = true;
            this.searchTextConvResults.searchedText = searchedText;
            this.searchTextConvResults.results = [];
            this.searchTextConvResults.totalCount = 0;
            // Get and sort room data from server
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/search?substring=${searchedText}&limit=${limit}`;
            const response = await fetch(url, { headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;

            const searchResults = (await response.json()).data;
            searchResults.forEach(async (data) => {
                const searchResult: SearchTextResult = new SearchTextResult(data.jid);
                if (data.isRoom && this.bubbleService) {
                    searchResult.room = this.bubbleService.getBubbleFromCache(data.jid);
                    if (!searchResult.room) {
                        try {
                            searchResult.room = await this.bubbleService.getBubble(data.jid);
                        } catch (error) {
                            this.logger.warn(`[conversationService] globalSearch -- room is missing ${data.jid}`);
                        }
                    }
                }
                else { searchResult.contact = this.contactService.getContactFromCacheByJid(data.jid); }

                if (searchResult.room || searchResult.contact) {
                    this.logger.debug(`[conversationService] globalSearch -- results found for ${data.jid} and count ${data.count}`);
                    searchResult.totalCount = data.count;
                    this.searchTextConvResults.results.push(searchResult);
                    this.searchTextConvResults.totalCount += data.count;
                }
            });
            this.searchTextConvResults.isSearching = false;
            return this.searchTextConvResults;
        }
        catch (error) {
            this.searchTextConvResults.isSearching = false;
            throw error;
        }
    }

    /**
     * Method used to search for text in a given conversation P2P or room. The searched text length should be 3+ characters.
     * This API will return a SearchTextResult that contains the list of IDs of messages to be searched after that
     * @param conversation - the conversation
     * @param searchedText - the text to be searched in the given P2P or room conversation
     * @param limit - number of results to get; Default value is 20;
     */
    public async searchForTextInConversation(conversation: Conversation, searchedText: string = "", limit: number = 20): Promise<SearchTextResult> {
        const peerJID = conversation.id;
        const isRoom = Boolean(conversation.bubble);
        if (searchedText.length < 3) { throw new Error("[conversationService] searchForTextInConversation -- min search length should be 3 characters"); }
        if (this.searchTextConvResults.isSearching) { throw new Error("[conversationService] searchForTextInConversation -- search already in progress"); }
        if (!peerJID) { throw new Error("[conversationService] searchForTextInConversation -- peerJID of the conversation is missing"); }
        try {
            this.searchTextConvResults.isSearching = true;
            this.searchTextConvResults.searchedText = searchedText;

            const searchResult: SearchTextResult = new SearchTextResult(peerJID);
            if (isRoom && this.bubbleService) {
                searchResult.room = this.bubbleService.getBubbleFromCache(peerJID);
                if (!searchResult.room) {
                    try {
                        searchResult.room = await this.bubbleService.getBubble(peerJID);
                    } catch (error) {
                        this.logger.warn(`[conversationService] globalSearch -- room is missing ${peerJID}`);
                    }
                }
            }
            else { searchResult.contact = this.contactService.getContactFromCacheByJid(peerJID); }

            if (!searchResult.room && !searchResult.contact) {
                throw new Error("[conversationService] searchForTextInConversation -- on P2P or room found with this peerJID");
            }

            // Get and sort room data from server
            const url = `${config.restServerUrl}/api/rainbow/enduser/v1.0/users/${this.contactService.userContact.dbId}/conversations/search/hits?substring=${searchedText}&peer=${peerJID}&isRoom=${isRoom}&limit=${limit}`;
            const response = await fetch(url, { headers: this.authService.getRequestHeader() });
            if (!response.ok) throw response;

            const searchResults = (await response.json()).data;
            for (let index = searchResults.length - 1; index >= 0; index--) {
                const data = searchResults[index];
                searchResult.addMsgItem(new SearchTextMsgResult(data.msgId, data.timestamp));
            }

            this.logger.debug(`[conversationService] searchForTextInConversation -- results found: ${searchResult.convRes.length}`);

            this.searchTextConvResults.isSearching = false;
            return searchResult;
        }
        catch (error) {
            this.searchTextConvResults.isSearching = false;
            throw error;
        }
    }

    /**
     * Method used to load the messages around a given timestamp. Can be used when we search for a message via API searchForTextInConversation
     * and then we want to load the message (or also the messages around it); This API will load the messages in the conversation list of messages and resolve once it's done;
     * @param conversation - the conversation where we want to load messages
     * @param timestamp - the timestamp of the message to be loaded
     * @param nbMessages - the number of messages to load before and after this one
     */
    public async loadHistoryMessagesAroundGivenMessage(conversation: ConversationRB, timestamp: number, nbMessages: number = 1): Promise<void> {
        await this.conversationServiceBulkHistoryHandler.getHistoryPageAroundMessage(conversation, timestamp, nbMessages);
    }

    // ************************************************ //
    // ** PUBLIC METHODS && PROPERTIES               ** //
    // ************************************************ //
    public async getConversation(convRef: string | User | Bubble | undefined, type?: 'contact' | 'room'): Promise<Conversation | undefined> {
        let conversation: ConversationRB | undefined;
        try {
            let id: string;
            let convType: string;
            if (!convRef) return undefined;
            if (convRef instanceof Contact) { id = (convRef).id; convType = 'contact'; }
            else if (convRef instanceof BubbleRB) { id = (convRef).dbId; convType = 'room'; }
            else { id = (convRef as string); convType = type; }
            conversation = this.getConversationFromCache(id);
            if (!conversation) conversation = await this.getOrCreateConversation(id, convType);
        }
        catch (error) {
            this.logger.error(`[conversationService] getConversation -- FAILURE -- ${error.message}`);
        }
        return conversation;
    }

    public getConversations(): Conversation[] {
        return Object.keys(this.conversations).map((key: string) => { return this.conversations[key]; });
    }

    public getCallConversations(): Conversation[] {
        return this.getConversations().filter((conv) => { return conv.call; });
    }

    public getConversationFromCache(id: string): ConversationRB | undefined {
        if (!this.conversations || !id) { return null; }

        // Get conversation by conversation id
        let conversation = this.conversations[id];

        // Get conversation by dbId
        if (!conversation) {
            const convKey = Object.keys(this.conversations).find((key: string) => { return this.conversations[key] && this.conversations[key].dbId === id; });
            conversation = convKey ? this.conversations[convKey] : null;
        }

        // Get conversation by room dbId
        if (!conversation) {
            const convKey = Object.keys(this.conversations).find((key: string) => { return this.conversations[key]?.room && this.conversations[key].room.dbId === id; });
            conversation = convKey ? this.conversations[convKey] : null;
        }
        return conversation;
    }

    public updateMissedCounters() {
        let missedIMCounter = 0;
        const missedIMDetails = [];

        Object.keys(this.conversations).forEach((key: string) => {
            const conversation = this.conversations[key];

            // Only add in global missed IM counter the active webinar missedIM
            if (conversation.bubble?.isWebinarBubble() && this.webinarService) {
                const conversationWebinarId = conversation.bubble?.conference?.webinar?.id;
                const activeWebinarId = this.webinarService?.conference?.activeWebinar?.id;
                if (conversationWebinarId === activeWebinarId && conversation.missedCounter !== 0) {
                    missedIMCounter += conversation.missedCounter;
                    missedIMDetails.push(conversation);
                }
            }
            else if (conversation.missedCounter !== 0) {
                missedIMCounter += conversation.missedCounter;
                missedIMDetails.push(conversation);
            }
        });

        if (missedIMCounter === this.missedInfoCounters.value.missedIMCounter) return;

        //if the user is not allowed to do IM, reset all missed im to 0
        if (this.contactService.userContact && !this.contactService.userContact.instantMessagesEnabled) {
            missedIMCounter = 0;
        }
        this.missedInfoCounters.value.missedIMCounter = missedIMCounter;
        this.missedInfoCounters.value.missedIMDetails = missedIMDetails;
        this.missedInfoCounters.next(this.missedInfoCounters.value);
    }

    // ************************************************ //
    // ** CAPABILITIES                               ** //
    // ************************************************ //

    public existAnotherCallForContact(call: CallRB): boolean {
        if (call?.conversationId !== call.id) {
            // don't try to use a temp conversation if the call is already attached to a rainbow conversation (not temp)
            return false;
        }
        if (!this.telephonyService) return false;
        const callsForContact = this.telephonyService.getCallsForContact(call.contact);
        const existOtherCallForContact = callsForContact.some((callForContact) => {
            return Boolean(callForContact.conversationId) && callForContact.id !== call.id;
        });
        return existOtherCallForContact;
    }

    public isTempTelephonyConversation(call: CallRB) {
        return !call.contact || call.contact.temp || call.contact.o365Flag || call.contact.outlookFlag || call.contact.companyDirectoryFlag || call.contact.personalDirectoryFlag || call.isConference || this.contactService.isUserContact(call.contact) || this.existAnotherCallForContact(call);
    }

    //USED ONLY FOR PBX / MP calls
    public async onCallEvent(call: CallRB, fromWebRtcGW: boolean = false) {
        if (!this.telephonyService || !this.webrtcGatewayService) {
            this.logger.warn('[ConversationService] telephony plugin is not available');
            return;
        }

        if (!call) return;

        if (call.callType === CallType.PHONE) {
            if (call.callStatus === CallStatus.ENDED) {
                //if (!call.conversationId && call.contact) { call.setConversationId(call.contact.id); } //CRRAINB-26782 + CRRAINB-26704 + CRRAINB-26539 // now managed in webrtcGateway.service
                this.logger.info('[ConversationService] onCallEvent -- ' + call + ' conversation: ' + call.conversationId);

                if (!call.conversationId) {
                    this.logger.error('[ConversationService] onCallEvent UNKNOWN for ' + call.id + '/ Anomaly no conversationId !');
                }

                const conv = this.getConversationFromCache(call.conversationId);
                if (conv) {
                    // Remove the temporary telephony conversation for this call (its id was the call id)
                    if (call.conversationId === call.id) {
                        this.logger.info('[ConversationService] onCallEvent ' + call.id + ' UNKNOWN, conversation ' + call.conversationId + ' : removeConversation');
                        this.removeLocalConversation(conv);
                    }
                    else {
                        // set conversation audio call to inactive
                        this.logger.info('[ConversationService] onCallEvent ' + call.id + ' UNKNOWN, conversation ' + call.conversationId + ' : set inactive');
                        this.resetAudioCall(conv);
                    }

                    conv.sendEvent(ConversationEvents.RAINBOW_CONVERSATION_CALL_UPDATE, { call });
                    this.updateAllCallsCapabilities();
                    // update my contact capabilities to recompute telephony and webrtc contact capabilities (mainly used by makecall)
                    this.computeCapabilitiesForMyContact();
                }

                //send update for the conversations
                this.sendEvent(ConversationServiceEvents.ON_CALL_IN_CONVERSATION_UPDATE, { conversation: conv, call });

                // Release webrtc calls (screen sharing) established with this contact (except if a pbx conference is established with the call contact)
                if (call.contact && !this.telephonyService?.getConferenceCallForContact(call.contact) && this.webrtcP2PService) {
                    this.webrtcP2PService.endAllCallsForContact(call.contact);
                }

                // If we are releasing a pbx conference call, we release also webrtc calls (screen sharing) established with any participant of the released conference
                if (call.isConference) {
                    // We defer the release of webrtc calls with participants by 1 second, to be compatible with the end of the conference on Oxo usecase,
                    // which clears the conference call, just before recreating a hold call with the participant: in this case , we don't clear the possible webrtc calls,
                    // because we actually stay in call with the participant
                    setTimeout(() => {
                        (call.participants || []).forEach((participantContact) => {
                            if (participantContact && this.telephonyService?.getActiveCallsForContact(participantContact).length === 0 && this.webrtcP2PService) {
                                this.webrtcP2PService.endAllCallsForContact(participantContact);
                            }
                        });
                    }, 1000);
                }
            }

            if (!fromWebRtcGW && this.webrtcGatewayService?.isMediaPillarUserSelected()) {
                if (!call.isSecondary) {
                    this.eventService.publish('ON_UPDATE_TELEPHONY_ICON_EVENT');
                    this.logger.info("[ConversationService] |" + (call.callType === CallType.PHONE ? "P|" : "W|") + call.id + "| onCallEvent -- event from handler IGNORED : MediaPillar usecase but not secondary call");
                    return;
                }
                // A ringing incoming phone call, in mediapillar usecase, is ignored, because the webrtcgateway service must first
                // ensure that both phone + webrtc incoming calls are incoming before opening the incoming call popup.
                if (call.callStatus === CallStatus.RINGING_INCOMMING) {
                    this.logger.info("[ConversationService] |" + (call.callType === CallType.PHONE ? "P|" : "W|") + call.id + "| onCallEvent -- event from handler IGNORED : incoming mediapillar call events are dispatched by gateway service");
                    this.updateAllCallsCapabilities(); // needed in case of transition QUEUED to DELIVERED
                    return;
                }
                if (call.callStatus === CallStatus.QUEUED_INCOMMING && call.getAutoOffHookInProgress()) {
                    this.logger.info("[ConversationService] |" + (call.callType === CallType.PHONE ? "P|" : "W|") + call.id + "| onCallEvent -- event from handler for QUEUED call with pickup in progress IGNORED");
                    return;
                }
            }

            if (call.callStatus !== CallStatus.ENDED && call.connectionId !== null) {
                // do not take into account second call if not allowed
                if (!this.isSecondCallAllowed) {
                    const calls = this.telephonyService.getNotIdleCalls();
                    if (calls.length > 1) {
                        if (calls.findIndex((calliter) => { return calliter.id === call.id; }) > 0) {
                            this.logger.info("[ConversationService] onCallEvent -- " + call + "=> event from handler IGNORED : number of calls not supported (feature Second Call not Allowed)");
                            return;
                        }
                    }
                }

                if (call.isHidden) {
                    this.logger.info("[ConversationService] onCallEvent -- " + call + "=> hidden call IGNORED : (used for hybrid blindtranfser)");
                    return;
                }

                // Fetch existing conversation for this call
                const isTempConversation = this.isTempTelephonyConversation(call);
                this.logger.info('[ConversationService] onCallEvent -- ' + call + ' isTempConversation: ' + isTempConversation);

                let conversation = isTempConversation ? this.getOrCreateTelephonyConversation(call) : this.getConversationFromCache(call.contact.id);

                // No conversation found : create it
                if (!conversation) {
                    if (isTempConversation) { conversation = this.getOrCreateTelephonyConversation(call); }
                    else { conversation = await this.getOrCreateConversation(call.contact.id, 'contact'); }
                }

                // update previous conference conversation if call is no more a conference
                if (isTempConversation && !call.isConference && call.conversationId === call.id && !conversation.contact && conversation.telephonyContacts) {
                    this.logger.info('[ConversationService] onCallEvent -- ' + call.id + ' : demote conference conversation to single conversation ' + call.id);
                    // previous temp conference conversation becomes temp single conversation
                    conversation.telephonyContacts = null;
                    conversation.contact = call.contact;
                    call.contact.conversation = conversation;
                    conversation.sendEvent(ConversationEvents.ON_CONVERSATION_CALL_CONTACT_CHANGE, { call });
                }

                //update contact for the temp conversations, as the contact is re-created each time
                if (conversation.contact?.temp && call.contact?.temp) {
                    if (call.contact.firstname || call.contact.lastname) {
                        conversation.contact.updateName(call.contact.firstname, call.contact.lastname);
                    }
                }

                // Remove previous conversation for this call or set previous conversation for this call inactive if the call contact has changed
                if (call.conversationId && call.conversationId !== conversation.id) {
                    // Call contact has changed (involving a conversation change for this call)
                    this.logger.info('[ConversationService] onCallEvent -- ' + call.id + ' : conversation changed id ' + call.conversationId + ' => id ' + conversation.id);

                    if (call.isInExternalWindow) {
                        //send event to the external window to manage the converstation switch for this call
                        this.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_ON_REPLACE_CONVERSATIONS_IN_EXTERNAL_POPOUT, { conversation, call });
                    }

                    if (call.conversationId === call.id) {
                        // Previous conversation for this call was a temporary telephony conversation (its id was the call id)
                        const previousConv = this.getConversationFromCache(call.id);
                        if (!isTempConversation && previousConv) {
                            // New conversation has a known contact: remove the previous temp conversation
                            this.logger.info('[ConversationService] onCallEvent -- ' + call.id + ' : remove prevous temp conversation ' + call.id);
                            this.resetAudioCall(previousConv);
                            this.removeLocalConversation(previousConv);

                            //??? remove local conversation will send event that is captured by the UI; we need to add the event between both
                            // if (this.activeConversation && this.activeConversation.id === previousConv.id) {
                            //     // remove previous active conversation to avoid dislay conversation flickering between active and recent conversations
                            //     hasCallContactChangedOnActiveConversation = true;
                            //     this.setActiveConversation(null);
                            // }
                        }
                    } else {
                        // Previous conversation for this call was a conversation with a rainbow contact: set it inactive
                        const previousConv = this.getConversationFromCache(call.conversationId);
                        if (previousConv) {
                            this.logger.info('[ConversationService] onCallEvent -- ' + call.id + ' : set previous conversation to known rainbow contact inactive ' + call.conversationId);
                            this.resetAudioCall(previousConv);
                            // if (this.activeConversation && this.activeConversation.id === previousConv.id) {
                            //     // remove previous active conversation to avoid dislay conversation flickering between active and recent conversations
                            //     hasCallContactChangedOnActiveConversation = true;
                            //     this.setActiveConversation(null);
                            // }
                            this.eventService.publish('ON_CONVERSATION_CALL_UPDATED_EVENT', { conversation: previousConv, call: call });
                        }
                    }
                }

                // Update conversation call
                this.updateConversationCall(conversation, call);
                this.updateAllCallsCapabilities();

                // if (call.callStatus === CallStatus.ACTIVE ||
                //     call.callStatus === CallStatus.DIALING ||
                //     call.callStatus === CallStatus.RINGING_OUTGOING ||
                //     call.callStatus === CallStatus.QUEUED_OUTGOING ||
                //     hasCallContactChangedOnActiveConversation) {

                //     // this.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_DISPLAY_CONVERSATIONS_IF_NECESSARY);
                //     const activeApp = this.rainbowService.activeApplication;
                //     if (activeApp === "easyAdmin" || activeApp === "admin") {
                //         this.rainbowService.setActiveApplication('conversations'); // switch from admin to conversations app
                //     }

                //     //DNI: To put a right timeout, but this code should not be HERE ! UI management should be removed from the services
                //     setTimeout(() => {
                //         // This timeout is needed to prevent ui-router errors because several router navigation calls are sometimes made in parallel for the conversation display management
                //         // Ex: displayConversation may be called for 3 calls in parallel when establishing a conference with 2 non rainbow users
                //         this.logger.info('[ConversationService] onCallEvent -- ' + call.id + ' : activate conversation ' + conversation.id);
                //         this.sendEvent('displayConversation', { conversation });
                //         if (conversation && call.isInExternalWindow) {
                //             this.eventService.publish('ON_CONVERSATION_CALL_UPDATED_EVENT', { conversation: conversation, call: call });
                //         }

                //     }, 400); // need more than 200ms when switching from admin to conversations app
                // }

                this.sendEvent(ConversationServiceEvents.ON_CALL_IN_CONVERSATION_UPDATE, { conversation, call });
                // this.orderConversations();
                // this.eventService.publish('ON_CONVERSATION_CALL_UPDATED_EVENT', { conversation: conversation, call: call });
                conversation?.sendEvent(ConversationEvents.RAINBOW_CONVERSATION_CALL_UPDATE, { call });

                // // if there is an active conversation (with an audioCall or videoCall) different from the conversation corresponding
                // // to the call in the event, we notify this active conversation so that the telCallAreaComponent may
                // //  recompute the items depending on other calls ( button recomputing , hold calls list refreshing...)
                // if (this.activeConversation && (this.activeConversation.audioCall || this.activeConversation.videoCall) && this.activeConversation.id !== conversation.id) {
                // this.activeConversation.sendEvent(ConversationEvents.RAINBOW_CONVERSATION_OTHERCALL_UPDATE, { call });
                // }

                // Update telephonyIcon
                const callEquipmentId = call.getDeviceIdFromConnectionIdFromCall(); //sipWise+
                if (callEquipmentId !== call.relevantEquipmentId && call.callStatus !== CallStatus.ACTIVE) {
                    //active state must be excluded of this filtering because only this state update the incomming popup
                    this.eventService.publish('ON_UPDATE_TELEPHONY_ICON_EVENT');
                    return;
                }

                if (call.callStatus === CallStatus.ACTIVE && call.isConference) {
                    // Release screen sharing webrtc calls established with the participants
                    (call.participants || []).forEach((participantContact) => {
                        if (participantContact && this.webrtcP2PService) {
                            this.webrtcP2PService.endAllCallsForContact(participantContact);
                        }
                    });
                }
            }

            if (call.status === CallRB.Status.ERROR && !call.connectionId) { //CRRAINB-40662 Missing following conversation.service refactoring
                this.sendEvent(ConversationServiceEvents.ON_CALL_IN_CONVERSATION_UPDATE, { call });
            }
        }
        else if (call.isWebrtcMpIncomingCallPopup) {
            //we've a case when we need to show the incoming WEBRTC MP call as no CSTA call exists (or it's late ...)
            this.logger.info(`[ConversationService] onCallEvent -- ${call.id} -- isWebrtcMpIncomingCallPopup and status ${call.callStatus}`);
            if (call.callStatus === CallStatus.RINGING_INCOMMING || call.callStatus === CallStatus.QUEUED_INCOMMING) {
                // Fetch existing conversation for this call
                const isTempConversation = this.isTempTelephonyConversation(call);
                this.logger.info('[ConversationService] onCallEvent -- ' + call + ' isTempConversation: ' + isTempConversation);

                let conversation = isTempConversation ? this.getOrCreateTelephonyConversation(call) : this.getConversationFromCache(call.contact.id);

                // No conversation found : create it
                if (!conversation) {
                    if (isTempConversation) { conversation = this.getOrCreateTelephonyConversation(call); }
                    else { conversation = await this.getOrCreateConversation(call.contact.id, 'contact'); }
                }

                // Update conversation call
                this.updateConversationCall(conversation, call);
                this.updateAllCallsCapabilities();
            }
            else {
                const conversation = this.getConversationFromCache(call.conversationId);
                if (conversation) {
                    const isTempConversation = this.isTempTelephonyConversation(call);

                    //we need to remove the local temporary conversation as a new one will be created by the CSTA
                    if (isTempConversation) {
                        delete this.conversations[conversation.id];
                        this.sendEvent(ConversationServiceEvents.ON_CONVERSATION_REMOVED, { conversation });

                        // To avoid leak
                        if (conversation.contact) {
                            conversation.contact.conversation = null;
                            conversation.contact = null;
                        }
                    }
                    else {
                        //if the conversation is not temporary, we need to remove only the video call as the CSTA will add the phone call
                        conversation.videoCall = null;
                    }

                    //send event for end of call only if the state went from incoming ringing to ended
                    if (call.callStatus === CallStatus.ENDED && conversation.videoCall) {
                        this.sendEvent(ConversationServiceEvents.ON_REMOVE_CALL_IN_CONVERSATION, { conversation });
                        this.updateAllCallsCapabilities();
                    }
                }
            }
        }
    }

    public allowDesktopSharing(): boolean {
        if (!window.adapter?.browserDetails) return true;
        return (window.adapter.browserDetails.browser !== 'safari');
    }

    private areWebrtcCallsWithSameContact(call1: CallRB, call2: CallRB) {
        return call1 && call2
            && call1.id !== call2.id
            && call1.callStatus !== CallStatus.ENDED
            && call1.callType === CallType.WEBRTC
            && call2.callType === CallType.WEBRTC
            && call1.contact && call2.contact
            && call1.contact.id === call2.contact.id;
    }

    public async updateCallInConversation(call: CallRB) {
        const conversation = await this.getOrCreateConversation(call.contact.id, 'contact');

        if (!this.areWebrtcCallsWithSameContact(conversation.call, call)) {
            // do not update call in conversation if the existing call in conversation is another call with the same contact
            // (usefull for specific usecase when 2 parties call each other in webrtc at the same time)
            this.updateConversationCall(conversation, call);
            conversation?.sendEvent(ConversationEvents.RAINBOW_CONVERSATION_CALL_UPDATE, { call });
            this.sendEvent(ConversationServiceEvents.ON_CALL_IN_CONVERSATION_UPDATE, { conversation, call });
        } else {
            this.logger.info(`[ConversationService] updateCallInConversation ignored for webrtc call ${call} because it already exists a webrtc call ${conversation.call} with this contact in the contact's conversation`);
        }

        //we need to update all other calls, as the current call update can impact other calls
        this.updateAllCallsCapabilities();
    }

    public updateAllCallsCapabilities() {
        Object.keys(this.conversations).forEach((key: string) => {
            const conversation: ConversationRB = this.conversations[key];

            if (conversation.call) {
                this.logger.debug(`[ConversationService] updateAllCallsCapabilities need updateCallCapabilities for call ${conversation.call}`);
                this.updateCallCapabilities(conversation.call);
            }
        });
    }

    // Check and update the call capabilities based on other calls / features
    public async updateCallCapabilities(call: CallRB) {
        if (call?.callType === CallType.WEBRTC) {
            this.updateWebrtcCallCapabilities(call);
        }
        if (call?.callType === CallType.PHONE) {
            this.updatePhoneCallCapabilities(call);
        }
    }

    private calculateListOfDestinations(call: CallRB, capabilities) {
        const webrtcCallsOnHold = this.webrtcP2PService.getWebrtcCallsOnHold();
        const activeWebCall = this.webrtcP2PService.getActiveWebrtcAudioCall();
        const activePstnCall = this.telephonyService?.getActiveCall();
        const callsOnHold = this.telephonyService?.getCallsOnHold();
        const ongoingOutgoingCall = this.telephonyService?.getOngoingOutgoingCall();

        if (call.callStatus === CallStatus.ACTIVE) {
            //put all calls on hold in the list
            if (webrtcCallsOnHold.length) { capabilities.transferCallDestinations = capabilities.transferCallDestinations.concat(webrtcCallsOnHold); }
            if (callsOnHold.length) { capabilities.transferCallDestinations = capabilities.transferCallDestinations.concat(callsOnHold); }
        }

        if (call.callStatus === CallStatus.RINGING_OUTGOING) {
            //put all calls on hold in the list
            if (callsOnHold.length) { capabilities.transferCallDestinations = capabilities.transferCallDestinations.concat(callsOnHold); }
        }

        if (call.callStatus === CallStatus.HOLD) {
            //put active call in the list
            if (activePstnCall && !activePstnCall.getIsVm()) { capabilities.transferCallDestinations.push(activePstnCall); }
            if (activeWebCall) { capabilities.transferCallDestinations.push(activeWebCall); }
            if (!this.telephonyService?.isSipWise() && ongoingOutgoingCall) { capabilities.transferCallDestinations.push(ongoingOutgoingCall); }
        }
    }

    private updateWebrtcCallCapabilities(call: CallRB) {
        const capabilities: CallCapabilities = {
            answer: false,
            release: false,
            deflectToVoicemail: false,
            addAudio: false,
            mute: false,
            unmute: false,
            addVideo: false,
            removeVideo: false,
            addSharing: false,
            removeSharing: false,
            blindTransfer: false,
            mergeCalls: false,
            addParticipantsToCall: false,
            retrieve: false,
            hold: false,
            dtmf: false,
            transferToOtherCall: false,
            transferCallDestinations: [],
            deflectToPhoneNumber: false,
            deflectPhoneNumberDestinations: [],
            startRecording: false,
            pauseRecording: false,
            resumeRecording: false,
            stopRecording: false,
            cancelRecording: false
        };

        const canUseAudio = this.contactService.userContact.webrtcAudioEnabled && call.contact.webrtcAudioEnabled;
        const canUseVideo = this.contactService.userContact.webrtcVideoEnabled && call.contact.webrtcVideoEnabled;
        const canUseSharing = this.contactService.userContact.webrtcSharingEnabled && call.contact.webrtcSharingEnabled;

        const activePstnCall = this.telephonyService?.getActiveCall();
        const pstnCallsOnHold = this.telephonyService?.getCallsOnHold();

        const webrtcCalls = this.webrtcP2PService?.getCurrentCallsNotMediaPillar();

        const callPutOnHold = Boolean(this.webrtcP2PService?.getCallPutOnHold() || this.telephonyService?.getCall(CallStatus.PUT_ON_HOLD));

        const canMakeTransferCall = Boolean(this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) && (call.contact.phoneInternalNumber || call.contact.phonePro));

        const recordingFeature = Boolean(this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_P2P_RECORDING) && this.contactService.userContact?.recordConversationEnabled && !this.horizonService?.vdiClientConnected && !this.citrixService?.vdiClientConnected);

        //in case of currenly outgoing ringing call, we cannot accept the incoming one
        const ongoingOutgoingPbxCalls = this.telephonyService ? this.telephonyService?.getCalls([CallStatus.DIALING, CallStatus.QUEUED_OUTGOING, CallStatus.RINGING_OUTGOING]) : [];
        const ongoingOutgoingCalls = this.webrtcP2PService.getCalls([CallStatus.DIALING, CallStatus.QUEUED_OUTGOING, CallStatus.RINGING_OUTGOING]).concat(ongoingOutgoingPbxCalls);

        const localMedias = {
            audio: Boolean(call.localMedia & CallMedia.AUDIO),
            video: Boolean(call.localMedia & CallMedia.VIDEO),
            sharing: Boolean(call.localMedia & CallMedia.SHARING)
        };

        switch (call.callStatus) {
            case CallStatus.DIALING:
            case CallStatus.ANSWERING:
            case CallStatus.CONNECTING:
                capabilities.release = true;
                break;

            case CallStatus.RINGING_INCOMMING:
                //we can reject the call / blind transfer ?
                capabilities.answer = !ongoingOutgoingCalls?.length;
                capabilities.release = true;
                break;

            case CallStatus.ACTIVE:
                capabilities.release = true;
                capabilities.addAudio = canUseAudio && !localMedias.audio;
                capabilities.mute = localMedias.audio && !call.isMuted;
                capabilities.unmute = localMedias.audio && call.isMuted;
                capabilities.addParticipantsToCall = Boolean((this.contactService.userContact.roomsEnabled || this.contactService.userContact.roomsAsVoiceEnabled) && (call.contact?.roomsEnabled || call.contact?.roomsAsVoiceEnabled) && !this.telephonyService?.getCalls().length
                    && (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CONFERENCE_ALLOWED) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_ADD_PARTICIPANT)));

                //RB Voice use cases
                if (this.telephonyService?.isSipWise()) {
                    capabilities.blindTransfer = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_BLIND_TRANSFER_CALL);
                    capabilities.transferToOtherCall = Boolean(!activePstnCall && (pstnCallsOnHold.length || webrtcCalls.length > 1) && canMakeTransferCall);
                    capabilities.mergeCalls = Boolean(!this.telephonyService?.isOfficeMode() && !activePstnCall && pstnCallsOnHold.length === 1 && webrtcCalls.length === 1 && this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS));
                }

                //double webrtc call
                if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_MANAGE_TWO_CALLS) || config.mixwebrtcpstn) {
                //check for other webrtc calls
                //if we've 2 calls are active or on hold, show merge calls button
                    if (webrtcCalls?.length === 2 && !activePstnCall && !pstnCallsOnHold.length
                        && (webrtcCalls[0].callStatus === CallStatus.ACTIVE || webrtcCalls[0].callStatus === CallStatus.HOLD)
                        && (webrtcCalls[1].callStatus === CallStatus.ACTIVE || webrtcCalls[1].callStatus === CallStatus.HOLD)) {
                        capabilities.mergeCalls = true;
                        capabilities.transferToOtherCall = true;
                    }
                }

                capabilities.addVideo = !localMedias.video && canUseVideo;
                capabilities.removeVideo = localMedias.video;
                capabilities.addSharing = !localMedias.sharing && canUseSharing && this.allowDesktopSharing();
                capabilities.removeSharing = localMedias.sharing;
                capabilities.hold = Boolean(this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS_FOR_PBX) || config.mixwebrtcpstn || config.mixwebrtcpstnhybride);

                if (recordingFeature && call.recorder?.state === CallRecordingState.RECORDING_STOPPED) { capabilities.startRecording = true; }
                if (call.recorder?.state === CallRecordingState.RECORDING_PAUSED) { capabilities.resumeRecording = true; capabilities.stopRecording = true; capabilities.cancelRecording = true; }
                if (call.recorder?.state === CallRecordingState.RECORDING_IN_PROGRESS) { capabilities.stopRecording = true; capabilities.pauseRecording = true; capabilities.cancelRecording = true; }

                break;

            case CallStatus.RELEASING:
            case CallStatus.ENDED:
            //do nothing
                break;

            case CallStatus.HOLD: {
                // We have put on hold the other party
                capabilities.release = true;
                capabilities.retrieve = !activePstnCall?.isConference && !callPutOnHold;

                //RB Voice use cases
                if (this.telephonyService?.isSipWise()) {
                    capabilities.transferToOtherCall = Boolean((activePstnCall || pstnCallsOnHold.length || webrtcCalls.length > 1) && canMakeTransferCall);
                    capabilities.mergeCalls = Boolean(!this.telephonyService?.isOfficeMode() && ((activePstnCall && !pstnCallsOnHold.length) || (!activePstnCall && pstnCallsOnHold.length === 1)) && webrtcCalls.length === 1 && this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS));
                }

                //double webrtc call
                if (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_MANAGE_TWO_CALLS) || config.mixwebrtcpstn) {
                //check for other webrtc calls
                //if we've 2 calls are active or on hold, show merge calls button
                    if (webrtcCalls?.length === 2 && !activePstnCall && !pstnCallsOnHold.length
                        && (webrtcCalls[0].callStatus === CallStatus.ACTIVE || webrtcCalls[0].callStatus === CallStatus.HOLD)
                        && (webrtcCalls[1].callStatus === CallStatus.ACTIVE || webrtcCalls[1].callStatus === CallStatus.HOLD)) {
                        capabilities.mergeCalls = true;
                        capabilities.transferToOtherCall = true;
                    }
                }

                if (call.recorder?.state === CallRecordingState.RECORDING_PAUSED || call.recorder?.state === CallRecordingState.RECORDING_IN_PROGRESS) { capabilities.stopRecording = true; capabilities.cancelRecording = true; }

                break;
            }

            case CallStatus.PUT_ON_HOLD: {
                // The other party put the call on hold
                capabilities.release = true;
                capabilities.removeSharing = Boolean(call.localMedia & CallMedia.SHARING);
                capabilities.removeVideo = Boolean(call.localMedia & CallMedia.VIDEO);

                if (call.recorder?.state === CallRecordingState.RECORDING_PAUSED || call.recorder?.state === CallRecordingState.RECORDING_IN_PROGRESS) { capabilities.stopRecording = true; capabilities.cancelRecording = true; }

                break;
            }
            default:
                break;
        }

        //manage doorphone case
        if (activePstnCall?.isDoorphoneCall || Boolean(pstnCallsOnHold.find((call) => { return call.isDoorphoneCall; }))) {
            //we've a doorphone call, so we need to remove all merge and transfer buttons
            capabilities.mergeCalls = false;
            capabilities.transferToOtherCall = false;
            capabilities.addParticipantsToCall = false;
        }

        if (capabilities.transferToOtherCall) {
            //calculate list of destinations
            this.calculateListOfDestinations(call, capabilities);
        }

        call.capabilities = capabilities;
    }

    private updatePhoneCallCapabilities(call: CallRB) {

        const capabilities: CallCapabilities = {
            answer: false,
            release: false,
            deflectToVoicemail: false,
            deflectToPhoneNumber: false,
            deflectPhoneNumberDestinations: [],
            addAudio: false,
            mute: false,
            unmute: false,
            addVideo: false,
            removeVideo: false,
            addSharing: false,
            removeSharing: false,
            blindTransfer: false,
            mergeCalls: false,
            addParticipantsToCall: false,
            retrieve: false,
            hold: false,
            dtmf: false,
            transferToOtherCall: false,
            transferCallDestinations: [],
            startRecording: false,
            pauseRecording: false,
            resumeRecording: false,
            stopRecording: false,
            cancelRecording: false
        };

        if (!this.telephonyService || !this.webrtcGatewayService) {
            call.capabilities = capabilities;
            return;
        }

        //TO DO:
        // make call ? --> I think it should be based on the telephony capability that should be calculated based on the flags + outgoing call, and not inside each call as it does not make sense

        const transferFeature = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_TRANSFER_CALL);
        const conferenceFeature = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_CONFERENCE_CALL);
        const mixWebrtcPstnFeature = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MIX_WEBRTC_PSTN_CALLS_FOR_PBX) || config.mixwebrtcpstn || config.mixwebrtcpstnhybride;

        const ongoingOutgoingCall = this.telephonyService?.getOngoingOutgoingCall();
        const activeCall: CallRB = this.telephonyService?.getActiveCall();
        const holdCalls: CallRB[] = this.telephonyService?.getCallsOnHold();
        const webrtcCalls = this.webrtcP2PService?.getCurrentCallsNotMediaPillar();
        const webrtcActiveCall = this.webrtcP2PService?.getActiveWebrtcAudioCall();

        const isAdhocConferenceAllowed = this.contactService.userContact.webrtcAudioEnabled && (this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CONFERENCE_ALLOWED) || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_ADD_PARTICIPANT))
            && (this.contactService.userContact.roomsEnabled || this.contactService.userContact.roomsAsVoiceEnabled) && (call.contact?.roomsEnabled || call.contact?.roomsAsVoiceEnabled);
        const isCCDAgent = this.ccdAgentService.isCcdAgentLogged();

        const isVMDeflectCallAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_DEFLECT_CALL);
        const isMediaPillarCall = this.webrtcGatewayService.isMediaPillarCallSituation(call);

        const deflectPhoneNumberDestinations: [{ id: string; number: string; }?] = [];

        const recordingFeature = Boolean(this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_P2P_RECORDING) && this.contactService.userContact?.recordConversationEnabled && !this.horizonService?.vdiClientConnected && !this.citrixService?.vdiClientConnected);

        // Handle secondaryCall
        let cannotAnswerSecondCall = false;
        let isAudioEnabled = true;

        //in case of currenly outgoing ringing call, we cannot accept the incoming one
        const ongoingOutgoingPbxCalls = this.telephonyService ? this.telephonyService?.getCalls([CallStatus.DIALING, CallStatus.QUEUED_OUTGOING, CallStatus.RINGING_OUTGOING]) : [];
        const ongoingOutgoingCalls = this.webrtcP2PService.getCalls([CallStatus.DIALING, CallStatus.QUEUED_OUTGOING, CallStatus.RINGING_OUTGOING]).concat(ongoingOutgoingPbxCalls);

        //check if we already have a webrtc call in PUT_IN_HOLD state, we cannot answer the call
        const callPutOnHold = Boolean(this.webrtcP2PService?.getCallPutOnHold() || this.telephonyService?.getCall(CallStatus.PUT_ON_HOLD));

        //calculate answer / deflect / reject buttons for INCOMING_RINGING / QUEUED_INCOMMING
        if (call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.RINGING_INCOMMING) {
            if (call.isSecondary) {
                isAudioEnabled = this.telephonyService.nomadicObject.destination === this.webrtcGatewayService.getMyMediaPillarRemoteExtension()
                    && this.webrtcGatewayService.getMediaPillarCallContext(call)
                    && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef
                    && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.callStatus !== CallStatus.ENDED; //CRRAINB-37068 from now isAudioEnabled also used if sipWise
                if (!this.telephonyService.isSipWise()) {
                    if (isMediaPillarCall && !this.telephonyService.oxoMultiCallMode()) {
                        if (call.callStatus === CallStatus.QUEUED_INCOMMING) {
                            // For OXE/OXO, in case of double incoming call, we should block the answer operation on the QUEUED_INCOMMING call,
                            // because the answer on the webrtc channel will generate, in the call server, the answer of the call in INCOMING_RINGING (eg CSTA DELIVERED) state.
                            // For OXE, the first call goes from INCOMING_RINGING to QUEUED_INCOMMING state:
                            // => the first call is the call of which the answer button is blocked.
                            // For OXO, the first call stays INCOMING_RINGING and the second call is presented in state QUEUED_INCOMMING:
                            // => the second call is the call of which the answer button is blocked.
                            const ringingIncomingCalls = this.telephonyService.getRingingIncomingCalls();
                            if (ringingIncomingCalls.length) {
                                cannotAnswerSecondCall = true;
                            }
                        }
                    }
                }
            }
        }

        if (this.telephonyService.isSipWise()) {
            if (this.contactService.userContact.mobilePro) { deflectPhoneNumberDestinations.push({ id: 'mobileProCan', number: this.contactService.userContact.mobileProCan }); }
            if (this.contactService.userContact.phonePerso) { deflectPhoneNumberDestinations.push({ id: 'phonePersoCan', number: this.contactService.userContact.phonePersoCan }); }
            if (this.contactService.userContact.mobilePerso) { deflectPhoneNumberDestinations.push({ id: 'mobilePersoCan', number: this.contactService.userContact.mobilePersoCan }); }
        }

        switch (call.callStatus) {
            case CallStatus.RINGING_INCOMMING:
                capabilities.answer = Boolean(!callPutOnHold && isAudioEnabled && !ongoingOutgoingCalls?.length);
                capabilities.deflectToVoicemail = Boolean(!this.telephonyService?.isSipWise() && !isCCDAgent && isVMDeflectCallAllowed && this.contactService.userContact.voicemailNumber && call.lastCause !== "MAKECALL" && call.lastCause !== "CALLBACK");

                if (this.telephonyService?.isSipWise()) {
                    capabilities.release = true;
                }
                else if (!capabilities.deflectToVoicemail && !isCCDAgent && !this.contactService.userContact.attendantConsoleEnabled) {
                //if no deflect and no CCD and no attendant, add release button
                    capabilities.release = true;
                }

                if (deflectPhoneNumberDestinations.length > 0 && !call.isHuntingGroupCallSipWise()) {
                    capabilities.deflectToPhoneNumber = true;
                    capabilities.deflectPhoneNumberDestinations = deflectPhoneNumberDestinations;
                }

                break;

            case CallStatus.QUEUED_INCOMMING:
                //state only exists for Hybrid systems
                capabilities.answer = Boolean(this.isSecondCallAllowed && !ongoingOutgoingCalls?.length && !cannotAnswerSecondCall && !callPutOnHold && isAudioEnabled && (this.telephonyService?.oxoMultiCallMode() || Boolean(activeCall) || holdCalls.length > 0)); // on hybrid system, not possible to answer queued call if no active (or hold) call
                capabilities.deflectToVoicemail = Boolean(!isCCDAgent && isVMDeflectCallAllowed && this.contactService.userContact.voicemailNumber && call.lastCause !== "MAKECALL" && call.lastCause !== "CALLBACK");

                if (!capabilities.deflectToVoicemail && !isCCDAgent && !this.contactService.userContact.attendantConsoleEnabled) {
                //if no deflect and no CCD and no attendant, add release button
                    capabilities.release = true;
                }

                break;

            case CallStatus.ANSWERING:
                capabilities.release = true;
                break;

            case CallStatus.DIALING:
            case CallStatus.QUEUED_OUTGOING:
                capabilities.release = true;
                capabilities.dtmf = this.webrtcGatewayService.isMediaPillarCallCase() && this.webrtcGatewayService.getMediaPillarCallContext(call) && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef
                    && (this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.callStatus === CallStatus.ACTIVE)
                    && !this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.isMuted;
                break;

            case CallStatus.RINGING_OUTGOING:
                capabilities.release = true;
                capabilities.transferToOtherCall = transferFeature && !this.telephonyService.isSipWise() && Boolean(holdCalls.length === 1) && !webrtcCalls.length;
                // capabilities.transfer = this.isTransferAllowed && Boolean(selectedCall) && (selectedCall.callStatus === CallStatus.HOLD) && !this.isSipWise();
                // capabilities.transferOrConferenceDestinations = capabilities.transferToOtherCall ? holdCalls.map((holdCall: CallRB) => holdCall.id) : [];

                //check DTMF
                capabilities.dtmf = this.webrtcGatewayService.isMediaPillarCallCase() && this.webrtcGatewayService.getMediaPillarCallContext(call) && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef
                    && (this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.callStatus === CallStatus.ACTIVE)
                    && !this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.isMuted;

                break;

            case CallStatus.ACTIVE:
                capabilities.release = true;
                capabilities.mute = Boolean(this.webrtcGatewayService?.getMediaPillarCallContext(call)
                    && this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef
                    && this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef.callStatus === CallStatus.ACTIVE
                    && !this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef.isMuted);
                capabilities.unmute = Boolean(this.webrtcGatewayService?.getMediaPillarCallContext(call)
                    && this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef
                    && this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef.callStatus === CallStatus.ACTIVE
                    && this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef.isMuted);

                if (!call.getIsVm() && !call.isConference && !call.isSfuPbxCall()) {
                    capabilities.hold = Boolean(!(isCCDAgent && this.telephonyService.nomadicObject.modeActivated && (this.telephonyService.getCalls().length > 1)));

                    //manage sip wise buttons a part from hybrid
                    if (this.telephonyService.isSipWise()) {
                        capabilities.blindTransfer = transferFeature;
                        if (!this.telephonyService?.isOfficeMode()) {
                            capabilities.addParticipantsToCall = Boolean(isAdhocConferenceAllowed && !(this.contactService.userContact?.attendantConsoleEnabled)) && !holdCalls.length && !webrtcCalls.length;
                            capabilities.mergeCalls = Boolean((conferenceFeature && holdCalls.length === 1 && !webrtcCalls.length) || (mixWebrtcPstnFeature && (webrtcCalls.length === 1 || holdCalls.length === 1)));
                        }

                        capabilities.transferToOtherCall = Boolean((transferFeature && holdCalls.length >= 1) || (mixWebrtcPstnFeature && webrtcCalls.length >= 1));
                    }
                    else {
                        capabilities.blindTransfer = transferFeature && (this.contactService.userContact.attendantConsoleEnabled || holdCalls.length === 0);
                        capabilities.mergeCalls = Boolean((conferenceFeature && holdCalls.length === 1));
                        capabilities.transferToOtherCall = Boolean((transferFeature && holdCalls.length === 1));
                    }
                }

                //check DTMF
                capabilities.dtmf = !call.isConference
                    && ((!this.webrtcGatewayService.isMediaPillarCallCase() && !this.telephonyService.isSipWise()) /* Office mode for hybrid system */
                     || (this.webrtcGatewayService.getMediaPillarCallContext(call)
                      && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef
                      && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.callStatus === CallStatus.ACTIVE
                        && !this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.isMuted));

                if (this.webrtcGatewayService?.getMediaPillarCallContext(call)
                        && this.webrtcGatewayService?.getMediaPillarCallContext(call).webrtcCallRef?.callStatus === CallStatus.ACTIVE) {
                    if (recordingFeature && call.recorder?.state === CallRecordingState.RECORDING_STOPPED) { capabilities.startRecording = true; }
                    if (call.recorder?.state === CallRecordingState.RECORDING_PAUSED) { capabilities.resumeRecording = true; capabilities.stopRecording = true; capabilities.cancelRecording = true; }
                    if (call.recorder?.state === CallRecordingState.RECORDING_IN_PROGRESS) { capabilities.stopRecording = true; capabilities.pauseRecording = true; capabilities.cancelRecording = true; }
                }

                // capabilities.makeCall = this.isSecondCallAllowed && !call.isConference && (!destinationContact || call.contact.jid !== destinationContact.jid);

                // capabilities.transfer = this.isTransferAllowed && Boolean(selectedCall) && (selectedCall.callStatus === CallStatus.HOLD) && !call.getIsVm();
                // capabilities.transferOrConferenceDestinations = (capabilities.transferToOtherCall || capabilities.conferenceWithOtherCall) ? holdCalls.map((holdCall: CallRB) => holdCall.id) : [];
                break;

            case CallStatus.HOLD:
                // We have put on hold the other party

                //if we've a distant call where we're PUT ON HOLD, no actions should be allowed
                if (!callPutOnHold) {
                    if (isCCDAgent) {
                        //manage CCD
                        //alternate and retrieve are now the same action for SDK.
                        //alternate is only available for HOLD calls, and the management is done inside the call.service.ts
                        capabilities.retrieve = Boolean(this.isSecondCallAllowed && !ongoingOutgoingCall && (this.telephonyService.isOfficeMode() || this.telephonyService.nomadicObject.modeActivated));
                        // this.buttons.alternate = this.isSecondCallAllowed && !ongoingOutgoingCall && this.isCCDAgent && this.telephonyService.nomadicObject.modeActivated && (this.telephonyService.getCalls().length > 1);
                    }
                    else {
                        //not CCD
                        capabilities.retrieve = Boolean(this.isSecondCallAllowed && !ongoingOutgoingCall);
                    }

                    if (!call.getIsVm() && !call.isConference && !call.isSfuPbxCall()) {

                        //manage sip wise buttons a part from hybrid
                        if (this.telephonyService.isSipWise()) {
                            if (!this.telephonyService?.isOfficeMode()) {
                                capabilities.mergeCalls = Boolean((conferenceFeature && holdCalls.length === 1 && activeCall && !activeCall.getIsVm()) || (mixWebrtcPstnFeature && !activeCall && !ongoingOutgoingCall && (webrtcCalls.length === 1 && holdCalls.length === 1)));
                            }
                            capabilities.transferToOtherCall = Boolean((transferFeature && ((activeCall && !activeCall.getIsVm()))) || (mixWebrtcPstnFeature && !activeCall && !ongoingOutgoingCall && webrtcActiveCall));
                        }
                        else {
                            capabilities.mergeCalls = Boolean((conferenceFeature && activeCall && !activeCall.getIsVm() && holdCalls.length === 1));
                            capabilities.transferToOtherCall = Boolean(transferFeature && ((activeCall && !activeCall.getIsVm()) || (ongoingOutgoingCall && !ongoingOutgoingCall.getIsVm())));
                        }
                    }
                }

                break;

            case CallStatus.PUT_ON_HOLD:
                // This call is put on hold by the other party
                capabilities.release = true;
                // capabilities.makeCall = this.isSecondCallAllowed && !ongoingOutgoingCall;
                break;

            case CallStatus.ERROR:
                // failed outgoing call
                capabilities.release = true;
                capabilities.dtmf = this.webrtcGatewayService.isMediaPillarCallCase() && this.webrtcGatewayService.getMediaPillarCallContext(call) && this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef
                    && (this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.callStatus === CallStatus.ACTIVE)
                    && !this.webrtcGatewayService.getMediaPillarCallContext(call).webrtcCallRef.isMuted;
                break;

            default:
                break;
        }

        //manage doorphone case
        if (activeCall?.isDoorphoneCall || Boolean(holdCalls.find((call) => { return call.isDoorphoneCall; }))) {
            //we've a doorphone call, so we need to remove all merge and transfer buttons
            capabilities.mergeCalls = false;
            capabilities.transferToOtherCall = false;
            capabilities.addParticipantsToCall = false;
        }

        if (capabilities.transferToOtherCall) {
            //calculate list of destinations
            this.calculateListOfDestinations(call, capabilities);
        }

        call.capabilities = capabilities;
    }

    public updateConversationCall(conversation: ConversationRB, call: CallRB) {
        // Update conversation
        let sendNewCallEvent = false;
        if (!conversation.call && call?.callStatus !== CallStatus.ENDED) {
            sendNewCallEvent = true;
        }

        if (call.callType === CallType.WEBRTC) {
            conversation.videoCall = call;

            // if (conversation.audioCall) {
            //     // Pbx call with webrtc screen sharing
            //     // We send the "othercall update" event on the conversation when we attach a videocall while an audio call is already attached :
            //     // this is the specific usecase of PBX call (audioCall) with webrtc screen sharing (videoCall) in the same conversation
            //     conversation.sendEvent(ConversationEvents.RAINBOW_CONVERSATION_OTHERCALL_UPDATE, { call: conversation.videoCall });
            // }
            if (conversation.audioCall && call.callStatus === CallStatus.RINGING_INCOMMING && call.remoteMedia === CallMedia.SHARING) {
                // auto accept incoming screen sharing for a conversation still in pbx audioCall
                this.logger.info("[ConversationService]   | updateConversationCall answerCall screen sharing: " + call.id);
                this.webrtcP2PService.answerCall(call);
            }

            //perhaps we can remove the call, but not right away; To be discussed.
            if (call.callStatus === CallStatus.ENDED) {
                //remove the call object after sending the update
                this.sendEvent(ConversationServiceEvents.ON_REMOVE_CALL_IN_CONVERSATION, { conversation });
                setTimeout(() => {
                    conversation.videoCall = null;
                }, 100);
            }
        }
        else if (call.callType === CallType.PHONE) {
            const prevConversationAudioCall: CallRB = conversation.audioCall;
            conversation.audioCall = call;
            if (prevConversationAudioCall !== call) {
                // we have changed the audio call of this conversation : notify event
                sendNewCallEvent = true;
            }
            if (sendNewCallEvent && this.contactService.userContact.isSoftphoneUCMode) {
                conversation.lastModification = new Date(); // in order to move the pbx conversation to the top of recent conversations
            }
        }

        // Update call
        call.setConversationId(conversation.id);

        if (sendNewCallEvent) {
            //update the call capabilities before sending the call update event
            this.updateCallCapabilities(call);
            //we've new call inside the conversation
            this.sendEvent(ConversationServiceEvents.ON_NEW_CALL_IN_CONVERSATION, { conversation });
        }

        // Update conversation capabilities
        if (!call.isConference && conversation.contact) { this.computeCapabilitiesForContact(conversation.contact); }
        this.computeCapabilitiesForMyContact();
        this.telephonyService?.computeTelCapabilities();
    }

    public resetAudioCall(conversation: ConversationRB) {
        if (conversation?.audioCall) {
            conversation.audioCall = null;
            // we have removed the audio call from the conversation : notify it
            this.sendEvent(ConversationServiceEvents.ON_REMOVE_CALL_IN_CONVERSATION, { conversation });
        }
    }

    public computeCapabilitiesForContact(user: User, skipUpdateConversation: boolean = false) {
        let telephony = false;
        let webRTC = false;
        let sharedDesktop = false;
        let addMedia = false;
        const contact = user as Contact;
        const contactStatus = contact.status;
        const fullJid = contact.fullJid;
        const myContact = this.contactService.userContact;

        //do not update
        if (contact.jid === myContact.jid) {
            this.computeCapabilitiesForMyContact();
            return;
        }

        //temporary workaround for Call hors roster
        const isContactInMyCompany = (contact.company && this.contactService.userContact.company && contact.company.name !== 'Rainbow' && contact.company.name !== 'Default' && contact.company.name === this.contactService.userContact.company.name);

        // Get eventual conversation with this contact
        const conversation = this.getConversationFromCache(contact.id);

        // Handle videoCall and sharedDesktop actions
        if (this.webrtcP2PService?.started) {
            const videoCall = conversation ? conversation.videoCall : null;
            if ((!videoCall || videoCall.callStatus === CallStatus.ENDED) && (((contactStatus === 'unknown' || contactStatus === 'wait') && isContactInMyCompany) || contactStatus === 'online' || contactStatus === 'online-mobile' || contactStatus === 'away')) {
                //if user is not in another webrtc call with this contact
                if (contact.useWebRTCOnlyIfMobileLogged) {
                    //the case of COBRI user, only allow webrtc calls in case of mobile presence seen for this contact

                    //check for mobile presence
                    let mobilePresence = false;
                    Object.keys(contact.resources).forEach((key: string) => {
                        if (key && key.indexOf("mobile_") !== -1) { mobilePresence = true; }
                    });
                    if (mobilePresence) {
                        if (contact.webrtcAudioEnabled || contact.webrtcSharingEnabled) {
                            webRTC = true;
                        }

                        sharedDesktop = true;
                    }
                }
                else {
                    if (contact.webrtcAudioEnabled || contact.webrtcSharingEnabled) {
                        webRTC = true;
                    }

                    sharedDesktop = true;
                }

            }
        }

        // Handle audioCall
        if (this.telephonyService?.started) {
            const phoneNumberAvailable = (contact.phonePro || (contact.phonePbx && contact.pbxId) || contact.mobilePro || contact.phonePerso || contact.mobilePerso);
            const audioCallsForContact = this.telephonyService.getActiveCallsForContact(contact);

            if ((audioCallsForContact.length === 0) && phoneNumberAvailable) {
                telephony = true;
            }
            //do not propose to add audio to sharing if user is already in pbx call with this online contact,
            //or user is in mediapillar call (whatever this call's contact)
            if (fullJid && ((audioCallsForContact.length !== 0) || (this.webrtcP2PService?.isInMediaPillarAudioCallWithContactJid(fullJid)))) {
                addMedia = true;
            }
        }

        // Compute conversation capability
        let capabilities = { telephony: telephony, webRTC: webRTC, sharedDesktop: sharedDesktop, mediaAvailable: (telephony || webRTC), addMedia: addMedia };

        // If Emily, remove the capabilities !
        if (contact.isBot || (contact.displayName && contact.displayName.indexOf('E-HELPER') !== -1)) {
            capabilities = { telephony: false, webRTC: false, sharedDesktop: false, mediaAvailable: false, addMedia: false };
        }

        // Update contact capabilities
        contact.capabilities = capabilities;

        // Update conversation capabilities
        if (conversation) {
            const previousCapabilities = Object.assign({}, conversation.capabilities);
            conversation.capabilities = capabilities;

            if (!skipUpdateConversation && !this.shallowEqual(previousCapabilities, capabilities)) {
                // do not send event if capabilities have not changed
                // this.eventService.publish('ON_CONVERSATIONS_UPDATED_EVENT', conversation, Conversation.EventType.CAPABILITIES);
                this.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_CAPABILITIES_UPDATE, { conversation });
            }
        }

    }

    public computeCapabilitiesForMyContact() {
        //can make or receive a telephony (PSTN) call
        let canMakeTelephonyCall = false;
        let canReceiveTelephonyCall = false;

        //can make or receive a webrtc standard call
        let webRTC = false;

        //can make a desktop sharing call on top of PBX call
        let sharedDesktop = false;

        //add new capability related to the mix PSTN & WebRTC calls that will be used
        //to ignore or allow receiving a second WEBRTC call based on the current user situation and profile
        //to avoid unwanted issues, by default the value is false
        let canReceiveSecondWebrtcCall = false;

        const myContact = this.contactService.userContact;

        //SFU web conference or WEBINAR
        const haveActiveConferenceSession = ((this.webConferenceService?.hasActiveWebConferenceSession()) || (this.webinarService?.conference?.hasActiveWebinarSession()));

        // Handle videoCall and sharedDesktop actions
        if (this.webrtcP2PService?.started) {
            //if there's an active conference, no webrtc call is allowed
            if (haveActiveConferenceSession) {
                webRTC = false;
            }
            else {
                const isInCallWebRtc = this.webrtcP2PService.isUserContactInCall();

                //user have no other webrtc calls (standard or MP)
                if (!isInCallWebRtc) {
                    //if I'm not in another webrtc call and user has the right to use webrtc audio
                    if (myContact.webrtcAudioEnabled || myContact.webrtcSharingEnabled) webRTC = true;
                    if (this.allowDesktopSharing()) sharedDesktop = true;
                }
                else {
                    //we already have 1 call, either WEBRTC or MP
                    if (this.manageTwoWebrtcCallsAllowed) {
                        //if we can manage 2 webrtc calls, allow webrtc
                        const numberOfCalls = this.webrtcP2PService.getCurrentActiveSessions().length;
                        if (numberOfCalls < 2 || (numberOfCalls === 2 && this.webrtcP2PService.getCurrentSharingCall())) {
                            //allow to make a second webrtc call
                            webRTC = true;
                        }
                    }
                    else if (this.isSecondWebrtcCallAllowed && this.webrtcP2PService.existMediaPillarAudioCall()) {
                        // In case of MP call in progress, we should allow to make a second webrtc call (available under feature flag only)
                        //check if we've only 1 telephony call
                        if (this.telephonyService && this.telephonyService.getCalls().length === 1) {
                            webRTC = true;
                        }
                    }
                    else if (this.isSecondWebrtcCallAllowedHybrid && !this.webrtcP2PService.existWebrtcActiveSessionNotMP()) {
                        //we can make webrtc call, in case we have 1 or 2 PSTN MP calls in progress, but no other webrtc

                        if (this.telephonyService && this.telephonyService.getCalls().length <= 2) {
                            webRTC = true;

                            //do not allow webrtc second call if the current telephony call is conference call
                            const activeTelephonyCall = this.telephonyService.getCalls()[0];
                            if (activeTelephonyCall?.isConference) {
                                webRTC = false;
                            }
                        }
                    }

                    // but the sharedDesktop capability must be evaluated if no sharing call already exists
                    if (this.webrtcP2PService.existMediaPillarAudioCall() && !this.webrtcP2PService.getCurrentSharingCall() && this.allowDesktopSharing()) {
                        sharedDesktop = true;
                    }
                }
            }

        }

        // TELEPHONY MANAGEMENT
        // User is in MP case
        if (this.telephonyService?.started && this.webrtcGatewayService?.isMediaPillarCallSituation()) {
            // handle mix PSTN & WebRTC case
            const telephonyCalls = this.telephonyService.getCalls(); // calls that are not ringing/queud incoming
            const hasActiveWebrtcCall = this.webrtcP2PService?.existWebrtcActiveSessionNotMP();

            //if user has attendant console role, do not put any limit on PSTN calls, but forbid makecall if only incoming calls or if outgoing ongoing cal or conference call
            if (this.contactService.userContact.attendantConsoleEnabled) { // Hybrid or Hub attendant user
                if (this.contactService.userContact.isRainbowHubUser()) {
                    canMakeTelephonyCall = !this.telephonyService.getOngoingOutgoingCall();
                    canReceiveTelephonyCall = true;
                } else if (this.contactService.userContact.isRainbowHybridUser()) {
                    // note: if only incoming calls exist (associated webrtc call is still ringing), makecall is forbidden
                    // Checking the presence of one active or hold ensures that the associated webrtc call is not ringing anymore

                    const allCalls: CallRB[] = this.telephonyService.getNotIdleCalls(); // all calls that are not ENDED
                    const activeOrHoldCalls: CallRB[] = this.telephonyService.getCalls([CallStatus.ACTIVE, CallStatus.HOLD]); /* user with one or more active/hold call(s) */
                    const outgoingCall: CallRB = this.telephonyService.getOngoingOutgoingCall();
                    const pbxConferenceCall: boolean = this.telephonyService.getActiveCall()?.isConference;

                    if (allCalls.length === 0
                        || /* free user */ (activeOrHoldCalls.length >= 1 && !outgoingCall && !pbxConferenceCall)) {
                        canMakeTelephonyCall = true;
                    }
                    canReceiveTelephonyCall = true;
                }

                //TO DO WITH LAURENT : define what can the OP do with webrtc call ?
                // if (this.isSecondWebrtcCallAllowed) {
                //     canReceiveSecondWebrtcCall = (telephonyCalls.length === 1);
                // }
            }
            //in case of active web conference, do not allow any incoming or outgoing call
            else if (haveActiveConferenceSession) {
                canMakeTelephonyCall = false;
                canReceiveTelephonyCall = false;
            }
            else {
                //user has no active conference and is not with Attendant Console role;

                //user can manage 2 webrtc calls
                if (this.manageTwoWebrtcCallsAllowed) { // Hub user
                    //if we can manage 2 webrtc calls, allow webrtc
                    const numberOfCalls = this.webrtcP2PService.getCurrentActiveSessions().length;
                    let nbMaxCalls: number = 2; //CRRAINB-41615 default value
                    if (this.contactService.userContact.isRainbowHubUser()) { //CRRAINB-41615 sipwise only
                        nbMaxCalls = (this.telephonyService.getTelephonyLinePolicy() === "multi_lines_plus"
                            ? this.profileService.getFeatureLimitMax(this.profileService.FeaturesEnum.TELEPHONY_MAX_CALL)
                            : 2);
                    }
                    if (numberOfCalls < nbMaxCalls || (numberOfCalls === nbMaxCalls && this.webrtcP2PService.getCurrentSharingCall())) {
                        canMakeTelephonyCall = true;
                        canReceiveTelephonyCall = true;
                        //allow to make a nth webrtc call
                        canReceiveSecondWebrtcCall = Boolean(numberOfCalls);
                    }
                }
                //user has the mix pstn & webrtc feature
                else if (this.isSecondWebrtcCallAllowed) { // Hub user
                    //user can have only 1 webrtc and 1 pstn call
                    if (hasActiveWebrtcCall) {
                        //if user has already one webrtc call, he can make / receive
                        //only if there're no pstn calls
                        canMakeTelephonyCall = !telephonyCalls.length;
                        canReceiveTelephonyCall = !telephonyCalls.length;
                    }
                    else {
                        //no webrtc call, check number of PSTN calls
                        if ((telephonyCalls.length === 0 && this.isBasicCallAllowed) || (telephonyCalls.length === 1 && this.isSecondCallAllowed)) {
                            canMakeTelephonyCall = true;
                            canReceiveTelephonyCall = true;
                        }
                        canReceiveSecondWebrtcCall = (telephonyCalls.length === 1);

                        //do not allow webrtc second call if the current telephony call is conference call
                        const activeTelephonyCall = this.telephonyService.getActiveCall();
                        if (activeTelephonyCall?.isConference) {
                            canReceiveSecondWebrtcCall = false;
                        }
                    }
                }
                else if (this.isSecondWebrtcCallAllowedHybrid) { // Hybrid user
                    //user can have only 1 webrtc and 1 or 2 pstn call

                    if ((telephonyCalls.length === 0 && this.isBasicCallAllowed) || (telephonyCalls.length >= 1 && this.isSecondCallAllowed)) {
                        canReceiveTelephonyCall = true;
                    }

                    const allCalls: CallRB[] = this.telephonyService.getNotIdleCalls(); // all calls that are not ENDED
                    const activeOrHoldCalls: CallRB[] = this.telephonyService.getCalls([CallStatus.ACTIVE, CallStatus.HOLD]); /* user with one or more active/hold call(s) */
                    const outgoingCall: CallRB = this.telephonyService.getOngoingOutgoingCall();
                    const pbxConferenceCall: boolean = this.telephonyService.getActiveCall()?.isConference;

                    // Note: if only incoming calls exist (associated webrtc call is still ringing), makecall is forbidden
                    if ((this.isBasicCallAllowed && allCalls.length === 0)
                        || /* free user */ (this.isSecondCallAllowed && activeOrHoldCalls.length >= 1 && !outgoingCall && !pbxConferenceCall)) {
                        canMakeTelephonyCall = true;
                    }

                    canReceiveSecondWebrtcCall = Boolean(!hasActiveWebrtcCall && telephonyCalls.length);

                    //do not allow webrtc second call if the current telephony call is conference call
                    const activeTelephonyCall = this.telephonyService.getActiveCall();
                    if (activeTelephonyCall?.isConference) {
                        canReceiveSecondWebrtcCall = false;
                    }
                }
                else {
                    //normal case, we put no limit on the outgoing / incoming calls as long as there's no webrtc call
                    //do not allow any PSTN MP (incoming or outgoing) call if the user has already an active webrtc call
                    if (!hasActiveWebrtcCall && (telephonyCalls.length === 0 && this.isBasicCallAllowed) || (telephonyCalls.length >= 1 && this.isSecondCallAllowed)) {
                        canMakeTelephonyCall = true;
                        canReceiveTelephonyCall = true;
                    }
                }
            }
        }
        else if (this.telephonyService?.started && this.webrtcGatewayService?.isMediaPillarUserSelected() && !this.webrtcGatewayService?.started) {
            //we're in computer mode but mp is not started
            canMakeTelephonyCall = false;
            canReceiveTelephonyCall = false;
        }
        else if (this.telephonyService?.started && !this.webrtcGatewayService?.isMediaPillarCallSituation()) {
            //we're in office phone mode (or just not in MP configuration)
            const telephonyCalls = this.telephonyService.getCalls();
            const webrtcGatewayEnabled: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_WEBRTC_PSTN_CALLING);

            if (myContact.isVirtualTerm && !webrtcGatewayEnabled) {
                canMakeTelephonyCall = false;
                canReceiveTelephonyCall = false;
            } else if ((telephonyCalls.length === 0 && this.isBasicCallAllowed) || (telephonyCalls.length >= 1 && this.isSecondCallAllowed)) {
                canReceiveTelephonyCall = true;
                canMakeTelephonyCall = true;
            }
        }

        // Compute conversation capability
        const capabilities = {
            telephony: canMakeTelephonyCall,
            telephonyReceive: canReceiveTelephonyCall,
            webRTC,
            sharedDesktop,
            mediaAvailable: (canMakeTelephonyCall || canReceiveTelephonyCall || webRTC),
            canReceiveSecondWebrtcCall };

        this.logger.debug('[ConversationService] computeCapabilitiesForMyContact  capabilities ' + JSON.stringify(capabilities));

        // Update contact capabilities
        myContact.capabilities = capabilities;
    }

    public shallowEqual(object1, object2) {
        const keys1 = Object.keys(object1);
        const keys2 = Object.keys(object2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (object1[key] !== object2[key]) {
                return false;
            }
        }
        return true;
    }

    public onReloadCapabilitiesForMyContactEvent(): void {
        this.logger.info('[conversationService] onReloadCapabilitiesForMyContactEvent');
        this.computeCapabilitiesForMyContact();
    }

    public onContactChangedEvent(contact: Contact) {
        this.computeCapabilitiesForContact(contact);
        this.computeCapabilitiesForMyContact();
    }

    public onTelephonyStateChangeEvent() {
        const conversations = this.getConversations() as ConversationRB[];
        if (conversations) {
            conversations.forEach((conversation) => {
                if (conversation.type === ConversationType.P2P) {
                    this.computeCapabilitiesForContact(conversation.contact);
                }
            });
        }
        this.computeCapabilitiesForMyContact();
    }

    public onRoomHistoryChangedEvent(room: BubbleRB) {
        if (room) {
            const conversation: ConversationRB = this.getConversationFromCache(room.jid);
            if (conversation) {
                conversation.resetHistoryInfo();
            }
        }
    }

    public onRoomAdminMessageEvent(roomJid: string, userJid: string, type: string, msgId: MessageID, extraParameters: any) {
        this.logger.info('[conversationService] onRoomAdminMessageEvent');

        const conversation = this.getConversationFromCache(roomJid);

        if (conversation && type === 'welcome' && conversation.bubble && conversation.bubble.owner) {
            userJid = conversation.bubble.owner.jid;
        }

        const contact = this.contactService.getContactFromCacheByJid(userJid);

        if (conversation && contact) {
            // If invitation msg and I'm not the owner
            if (!conversation.bubble.isConnecteUserOwner() && type === 'invitation') return;
            if (conversation.bubble?.isWebinarBubble()) return;
            // Add message
            let translatedMessage = '';
            if (type === 'voicemessage') {
                translatedMessage = this.i18n.translate('huntingGroupVmCounters', { newMsgs: extraParameters.newMsgs, oldMsgs: extraParameters.oldMsgs });
            }
            if (type === 'pollPublish') {
                this.conversationServiceEventHandler.handlePollEvent(conversation, contact, msgId, extraParameters?.pollId);
            }
            this.conversationServiceEventHandler.onRoomAdminMessageReceived(conversation, contact, type, msgId, translatedMessage);
        }
    }

    public onMyContactChangedEvent() {
        const conversations = this.getConversations() as ConversationRB[];
        conversations.forEach((conversation) => {
            if (conversation.type === ConversationType.P2P) {
                this.computeCapabilitiesForContact(conversation.contact);
            }
        });
        this.computeCapabilitiesForMyContact();
    }

    /* *******************************************************************/
    /* * Remove the conversation history test                                */
    /* ********************************************************************/

    public async reinit() {
        if (!this.reinitInProgress) {
            this.reinitInProgress = this.reinitInternal();
        }

        return this.reinitInProgress;
    }

    public async reinitInternal(delay: number = 10000) {
        try {

            if (delay > 20000) { delay = 20000; }
            this.logger.info('[conversationService] reinitInternal conversation service');

            Object.keys(this.conversations).forEach((key: string) => {
                const conversation = this.conversations[key];
                conversation.resetHistoryInfo();
            });

            if (!this.conversationCreationPromise) { this.conversationCreationPromise = {}; }

            // Fetch conversations from server
            await this.getServerConversationsAndUpdate();
            this.linkAllActiveCallsToConversations();
            this.computeCapabilitiesForMyContact();

            // Reset the missed im counter
            this.missedInfoCounters.next(this.missedInfoCounters.value);

            this.logger.info('[conversationService] reinitInternal conversation service -- success');
            this.sendEvent(ConversationServiceEvents.ON_SERVICE_STARTED);

            this.updateMissedCounters();

            this.reinitInProgress = null;
        }
        catch (error) {
            this.logger.info(`[conversationService] reinitInternal failure, try again in ${delay}`);
            this.reinitTimer = setTimeout(async () => {
                this.reinitInternal(delay * 2);
            }, delay);
        }
    }

    private linkAllActiveCallsToConversations() {
        // Handle current calls (FIXME : CERTAINLY NOT AT THE RIGHT PLACE!!!!)
        if (this.telephonyService?.calls) {
            Object.keys(this.telephonyService.calls).forEach((key: string) => {
                const call = this.telephonyService.calls[key];
                this.eventService.publish('ON_CALL_UPDATED_EVENT', call);
            });
        }
        if (this.webrtcP2PService?.calls) {
            Object.keys(this.webrtcP2PService.calls).forEach((key: string) => {
                const call = this.webrtcP2PService.calls[key];
                this.eventService.publish('ON_CALL_UPDATED_EVENT', call);
            });
        }
    }

    public cleanUnwantedConversations() {
        Object.keys(this.conversations).forEach((key) => {
            const conversation = this.conversations[key];

            if (conversation.type === ConversationType.BUBBLE) {
                if (!this.contactService.userContact.roomsEnabled && !this.contactService.userContact.roomsAsVoiceEnabled) {
                    this.logger.info('cleanUnwantedConversations -- close conversation ' + conversation.id + ' due to customisation limitation');
                    this.removeConversation(conversation);
                }
            }
        });
    }

    /**
     * AddChatMessage
     * @param conversation - the conversation
     * @param from - the from contact
     * @param date - ???
     * @param data - the message content
     * @param messageID - the message identifier
     * @param isSender - ???
     * @param subject - ???
     * @param answeredMsgId - ???
     * @param answeredMsgDate - ???
     * @param additionalContent - ???
     * @param mentions - ???
     * @param urgency - ???
     * @param isForwarded - ???
     */
    public addChatMessage(conversation: ConversationRB, from: Contact, date: Date, data: string, messageID: MessageID, isSender: boolean, subject, answeredMsgId, answeredMsgDate, additionalContent: MessageAdditionalContent, mentions: (Mention | MentionLegacy)[], urgency?, isForwarded?): Message {
        const existingMsg = conversation.getMessageById(messageID);
        if (existingMsg) {
            this.logger.info('[ConversationService] addChatMessage -- ' + conversation.id + ' add CHAT message (' + messageID + ') already exists.');
            return null;
        }

        this.logger.info('[ConversationService] addChatMessage -- ' + conversation.id + ' add CHAT message (' + messageID + ') from ' + from.jid);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message = Message.create(messageID, date, from, side, data, additionalContent, subject, answeredMsgId, answeredMsgDate, mentions);
        message.urgency = urgency;
        message.isForwarded = isForwarded;
        if (side === MessageSide.RIGHT) {
            //check if I've send the message
            if (isSender) message.setReceiptStatus(MessageStatus.IN_PROGRESS);
            else message.setReceiptStatus(MessageStatus.SENT);
        }
        else {
            //in case of incoming message, update to unread
            this.sendAckReceivedMessage(conversation, message);
        }

        if (message.isAdaptiveCardResponse()) {
            const messageObject = JSON.parse(message.additionalContent.message);
            if (messageObject.rainbow && messageObject.rainbow.text === '') return message;
        }

        return this.addMessage(conversation, message);
    }

    /**
     * addAdminBubbleMessage
     * @param conversation - the conversation
     * @param from - the message from contact
     * @param date - the date of the message
     * @param type - the type of message
     * @param messageId - the message identifier
     * @param translatedMessage -the translatedMessage
     */
    public addAdminBubbleMessage(conversation: ConversationRB, from: Contact, date: any, type: string, messageId: MessageID, translatedMessage: any = ''): Message {
        const existingMsg: Message = conversation.getMessageById(messageId);
        if (existingMsg) {
            this.logger.info('[ConversationService] addAdminBubbleMessage -- ' + conversation.id + ' add addAdminBubbleMessage message (' + messageId + ') already exists.');
            return null;
        }

        this.logger.info('[ConversationService] addAdminBubbleMessage -- ' + conversation.id + ' add addAdminBubbleMessage from ' + from.jid + ' : ' + type);
        let data = type + 'MsgRoom';
        if (type === 'voicemessage') data = translatedMessage;

        const message = Message.create(messageId, date, from, MessageSide.ADMIN, data);
        message.setReceiptStatus(MessageStatus.SENT);
        return this.addMessage(conversation, message);
    }

    private addFSMessage(conversation: ConversationRB, fileId: string, data: string, attachedInfos: any, geoLoc: GeoLoc = null, urgency: Urgency = null): Message {
        const messageToSendID = ConversationRB.getUniqueMessageId();
        const date = new Date();
        const side = MessageSide.RIGHT;

        this.logger.info('[ConversationService] addFSMessage --' + conversation.id + ' add FILE SHARING message from me');
        const message = Message.createFileSharingMessage(messageToSendID, date, this.contactService.userContact, side, data, fileId, data, attachedInfos, geoLoc);
        message.urgency = urgency;
        message.setReceiptStatus(MessageStatus.IN_PROGRESS);

        return this.addMessage(conversation, message);
    }

    private addForwardChatMessage(conversation: ConversationRB, from: Contact, date: Date, messageID: MessageID, forwardedMessage: any, origConv: any): Message {
        const existingMsg = conversation.getMessageById(messageID);
        if (existingMsg) {
            this.logger.info("[Conversation] " + conversation.id + " add ForwardChat message (" + messageID + ") already exists.");
            return null;
        }

        this.logger.info("[Conversation] " + conversation.id + " add ForwardChat message (" + messageID + ") from " + from.jid);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;

        // We need to prepare an other Message to store in the other conversation
        const msgFwdOtherConv: Message = Message.createFromMsg(forwardedMessage);
        msgFwdOtherConv.id = messageID;
        msgFwdOtherConv.side = side;
        msgFwdOtherConv.date = date;
        msgFwdOtherConv.from = from;
        msgFwdOtherConv.isForwarded = true;
        msgFwdOtherConv.showIdentity = forwardedMessage.showIdentity;
        msgFwdOtherConv.forwardedMsg = forwardedMessage;
        msgFwdOtherConv.setReceiptStatus(MessageStatus.IN_PROGRESS);
        msgFwdOtherConv.forwardConvType = (origConv.type === ConversationType.P2P) ? 'chat' : 'groupchat';
        this.addMessage(conversation, msgFwdOtherConv);

        return msgFwdOtherConv;
    }

    public addRecordingMessage(conversation: ConversationRB, from: Contact, date: Date, data: any, messageId: MessageID): Message {
        const message = Message.createRecordingAdminMessage(messageId, date, from, data);
        return this.addMessage(conversation, message);
    }

    public addFileMessage(conversation: ConversationRB, from: Contact, date: any, data: any, messageID: any, urgency: Urgency = null): Message {
        this.logger.info("[Conversation] " + conversation.id + " add FILE message from " + from.jid + " : " + data);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message = Message.createFileMessage(messageID, date, from, side, data, false);
        message.urgency = urgency;
        return this.addMessage(conversation, message);
    }

    public addFileSharingMessage(conversation: ConversationRB, from: any, date: any, data: any, messageID: any, fileId: any, fileName: any, geoloc: any, urgency: Urgency, isForwarded: boolean): Message {
        this.logger.info("[Conversation] " + conversation.id + " add FILE SHARING message from " + from.jid + " : " + fileId);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message: Message = Message.createFileSharingMessage(messageID, date, from, side, data, fileId, fileName, null, geoloc);
        message.urgency = urgency;
        message.isForwarded = isForwarded;
        return this.addMessage(conversation, message);
    }

    public addWebRTCMessage(conversation: ConversationRB, from: Contact, date: any, data: any, messageID: any): Message {
        const existingMsg = conversation.getMessageById(messageID);
        if (existingMsg) {
            this.logger.info("[Conversation] " + conversation.id + " add addWebRTCMessage message (" + messageID + ") already exists.");
            return null;
        }

        //this.logger.info("[Conversation] " + this.id + " add WebRTC message from " + from.jid + " : " + data);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message = Message.createWebRTCMessage(messageID, date, from, side, data, false);
        return this.addMessage(conversation, message);
    }

    public addPinMessage(conversation: ConversationRB, from: Contact, date: any, data: any, messageID: any): Message {
        const existingMsg = conversation.getMessageById(messageID);
        if (existingMsg) {
            this.logger.info("[Conversation] " + conversation.id + " addPinMessage (pinId: " + messageID + ") already exists.");
            return null;
        }
        this.logger.info("[Conversation] " + conversation.id + " addPinMessage from " + from.jid + " : " + data);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message = Message.createPinMessage(messageID, date, from, side, data);
        return this.addMessage(conversation, message);
    }

    public addShareRecapMessage(conversation: ConversationRB, from: Contact, date: any, data: any, messageID: any, additionalContent: MessageAdditionalContent): Message {
        const existingMsg = conversation.getMessageById(messageID);
        if (existingMsg) {
            this.logger.info("[Conversation] " + conversation.id + " addShareRecapMessage (pinId: " + messageID + ") already exists.");
            return null;
        }
        this.logger.info("[Conversation] " + conversation.id + " addShareRecapMessage from " + from.jid + " : " + data);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message = Message.createShareRecapMessage(messageID, date, from, side, data, additionalContent);
        return this.addMessage(conversation, message);
    }

    public addPollMessage(conversation: ConversationRB, from: Contact, date: any, data: any, messageID: any, additionalContent: MessageAdditionalContent): Message {
        const existingMsg = conversation.getMessageById(messageID);
        if (existingMsg) {
            this.logger.info("[Conversation] " + conversation.id + " addPollMessage (pollId: " + messageID + ") already exists.");
            return null;
        }
        this.logger.info("[Conversation] " + conversation.id + " addPollMessage from " + from.jid + " : " + data);
        const side = this.contactService.isUserContact(from) ? MessageSide.RIGHT : MessageSide.LEFT;
        const message = Message.createPollMessage(messageID, date, from, side, data, additionalContent);
        return this.addMessage(conversation, message);
    }

    private addMessage(conversation: ConversationRB, message: Message): Message {

        // Check if this message already exist in message store
        if (conversation.messages.find((item: Message) => { return item.id === message.id; })) {
            this.logger.info("[ConversationService] addMessage -- " + conversation.id + " try to add an already stored message with id " + message.id);
            return message;
        }

        // Increment unreadEvent counter
        if (message.side === MessageSide.LEFT) {
            if (message.type === MessageType.WEBRTC) { conversation.missedCalls += 1; }
            this.setStatusMessage(conversation, message.from, ConversationStatus.ACTIVE);
        }

        // Store the message
        conversation.messages.push(message);

        // Update lastModification
        conversation.lastModification = new Date();

        // Update lastMessageText
        conversation.lastMessage.text = message.data;
        conversation.lastMessage.sender = message.from.id;
        conversation.lastMessage.messageId = null;
        conversation.forwardFrom = message.isForwarded ? message.from.jid : null;
        conversation.forwardedMessage = message.isForwarded ? message.data : null;
        conversation.isForwardedMessageFile = Boolean(message.isForwarded && message.fileName);
        conversation.lastMessage.side = message.side;

        // Update fileDescriptor if necessary
        if (message.fileId) {
            const fileDescriptor = this.fileStorageService.getFileDescriptorById(message.fileId);
            if (fileDescriptor && !fileDescriptor.isRainbowStorage() && fileDescriptor.fileName !== message.fileName) fileDescriptor.fileName = message.fileName;
        }

        // Send events
        conversation.sendEvent(ConversationEvents.ON_MESSAGES_UPDATED, undefined);
        conversation.sendEvent(ConversationEvents.ON_MESSAGE_ADDED, { message });
        this.sendEvent(ConversationServiceEvents.ON_MESSAGE_ADDED_IN_CONVERSATION, { conversation, message });
        return message;
    }

    public setStatusMessage(conversation: ConversationRB, from: Contact, status: ConversationStatus): void {
        this.logger.info(`[ConversationService] setStatusMessage-- ${conversation.id} add status message from ${from.jid} : ${status} `);
        if (conversation.participantStatuses[from.jid] !== status) {
            conversation.participantStatuses[from.jid] = status;

            const composingParticipants = Object.entries(conversation.participantStatuses).filter(([_, value]) => value === ConversationStatus.COMPOSING);
            const composers = composingParticipants.length > 0
                ? composingParticipants.map(([jid]) => this.contactService.getContactFromCache(jid))
                : null;

            conversation.sendEvent('ON_STATUS_CHANGED', { status, composers });
        }
    }

    public onCallStatusChange(call: CallRB): void {
        if (!call) return;
        if (call.isWebRtcMediaPillar()) return;
        if (call?.isSecondSharingCall) return;

        if (call.callStatus === CallStatus.ACTIVE
            || call.callStatus === CallStatus.RINGING_OUTGOING
            || call.callStatus === CallStatus.QUEUED_OUTGOING
            || call.callStatus === CallStatus.DIALING) {

            if (this.activeCall?.id !== call.id) {
                this.activeCall = call;
                // Send on active call change event
                const conversation = this.getConversationFromCache(call.conversationId);
                this.sendEvent(ConversationServiceEvents.ON_ACTIVE_CALL_CHANGE, { conversation, call });
            }
        }
        else {
            if (call.callStatus === CallStatus.ERROR && this.activeCall?.id === call.id) {
                //in case of error, check if the current active call is the same
                //do nothing as we can still send DTMF in case of error
            }
            // Check if this is the current active call
            else if (this.activeCall?.id === call.id) {
                this.activeCall = null;
                // Send on active call change event
                this.sendEvent(ConversationServiceEvents.ON_ACTIVE_CALL_CHANGE);
            }
        }
    }

    private shortnameToUnicode(text: string): string {
        if (!this.emojiService) return text;
        return this.emojiService.shortnameToUnicode(text);
    }

    /**
     * Process stanza object to extract mentions
     * @param body - message body to process
     * @param mentionElem - mention XMPP XML element containing the mentions
     * @returns
     */
    public processStanzaMentions(body: string, mentionElem: XMLElements): { body: string; mentions: Mention[]; } {
        const mentions: Mention[] = [];
        const legacyMentions: MentionLegacy[] = [];

        const mentionJidElem = mentionElem.find("jid");
        for (const content of mentionJidElem) {
            // Get the JID
            const jid = content.text();
            // If there is no JID, skip the mention
            if (!jid) continue;

            // Get the contact from the JID
            const contact = this.contactService.getContactFromCacheByJid(jid);
            // If there is no contact, skip the mention
            if (!contact) continue;

            const pos = content.attr('pos') !== null ? parseInt(content.attr('pos'), 10) : null;
            const size = content.attr('size') !== null ? parseInt(content.attr('size'), 10) : null;

            if (pos === null || size === null) {
                const mention = Mention.create(contact.dbId, contact.displayName);
                mentions.push(mention);
                continue;
            }

            // If there is a `pos` and a `size`, transform the message to use the new format
            const mention = MentionLegacy.create(contact.dbId, contact.jid, pos, size, contact.displayName);
            legacyMentions.push(mention);
        }

        // Sort mentions in the reverse order (last in first)
        legacyMentions.sort((mentionA, mentionB) => mentionB.position - mentionA.position);

        // Convert legacy mentions into proper mentions
        for (const legacyMention of legacyMentions) {
            const beforeStr = body.substring(0, legacyMention.position);
            const afterStr = body.substring(legacyMention.position + legacyMention.size);

            const newMention = Mention.create(legacyMention.dbId, legacyMention.displayName);
            body = beforeStr + formatMention(newMention) + afterStr;
            mentions.push(newMention);
        }

        return { body, mentions };
    }

    public processStanzaReaction(reactionsElem: XMLElements): Map<string, Reaction> {
        const reactions = new Map();
        for (const reactionElem of reactionsElem) {
            // Get the Jid
            const jid = reactionElem.attr('from');
            if (!jid) continue;
            // Get the reaction concatenated emojis
            const emojis = reactionElem.text();

            const reaction = Reaction.create(jid, emojis);
            reactions.set(jid, reaction);
        }
        return reactions;
    }

    public processStanzaReactionsCount(reactionsCountElem: XMLElements): Map<string, number> {
        const reactionsCount: Map<string, number> = new Map<string, number>(null);

        for (const reactionCountElem of reactionsCountElem) {
            // Get the reaction emoji
            const emoji = reactionCountElem.attr('reaction');
            if (!emoji) continue;

            // Get the count (i.e. the number of times this has been used)
            const count = Number(reactionCountElem.text());
            if (count < 1) continue;

            reactionsCount.set(emoji, count);
        }
        return reactionsCount;
    }

    /**
     * Transform mention strings back to normal string text
     * @param message - the message object
     * @param body - the message string
     */
    public transformMessageToText(body: string): string {
        let rawText = body;
        // Reset the RegExp
        MENTION_REGEX.lastIndex = 0;

        let match: RegExpMatchArray;
        let constructedText = '';

        // Loop on every mention found
        while ((match = MENTION_REGEX.exec(rawText)) !== null) {
            // If a mention is found, extract the display name and the db id
            let displayName = match[1];
            const dbId = match[2];

            // Try to update the display name with the contact information (if available), and sanitize it
            const contact = this.contactService.getContactFromCacheById(dbId);
            if (contact) { displayName = contact.displayName; }
            displayName = escape(displayName);

            // Extract the text before the mention and appens both of them to a new string value
            const beforeStr = rawText.substring(0, match.index);
            constructedText += `${beforeStr}@${displayName}`;
            // Finally, update the rawText to the remaining string to process
            rawText = rawText.substring(match.index + match[0].length);
            // Reset the regex index
            MENTION_REGEX.lastIndex = 0;
        }
        // Appens the remaining `rawText` that hasn't been processed to the new string
        constructedText += rawText;
        return constructedText;

    }

}

/**
 * @internal
 * This interface describes the data received from server when creating / getting conversation
 */
export interface ConversationServerData {
    /**
     * The JID IM of the distant party
     */
    jid_im?: string;
    /**
     * The DB ID of the conversation itself
     */
    id?: string;
    /**
     *The type of the conversation (user, room, bot)
     */
    type?: string;
    /**
     * The last message date in string
     */
    lastMessageDate?: string;
    /**
     * The last msg text in the conversation
     */
    lastMessageText?: string;
    /**
     * if the conversation is muted
     */
    mute?: boolean;
    /**
     * The number of not ACK READ read IMs
     */
    unreadMessageNumber?: string;
    /**
     * The creation date of the conversation in string
     */
    creationDate?: string;
    /**
     * The JID IM of the user that send the last message
     */
    lastMessageSender?: string;
    /**
     * In case of call event in the conversation (P2P only)
     */
    call?: null | object;
    /**
     * If the last message was modified
     */
    modified?: boolean;
    /**
     * The JID IM of the user that forwarded the messages
     */
    forwardFrom?: string;
    /**
     * The forwarded message
     */
    forwardedMessage?: string;
    /**
     * If the user in the conversation has the right to use IM (p2p only)
     */
    instantMessagesCustomisation?: string;
    /**
     * If the user in the conversation has the right to ACK messages (p2p only)
     */
    readReceiptsCustomisation?: string;
    /**
     * If the user in the conversation has the right to use screen sharing
     */
    useScreenSharingCustomisation?: string;
    /**
     * If the user in the conversation has the right to use audio
     */
    useWebRTCAudioCustomisation?: string;
    /**
     * If the user in the conversation has the right to use video sharing
     */
    useWebRTCVideoCustomisation?: string;
    /**
     * The filename
     */
    filename?: string;
    /**
     * If the last messages is geoloc
     */
    geoloc?: boolean;
    /**
     * The last message content type (for markdown things)
     */
    contentType?: string;
    /**
     * Bookmark infos for the conversatiokn
     */
    bookmark?: ConversationBookmark;
    /**
     * The topic (description) of the bubble
     */
    topic?: string;
    /**
     * The type of the last event (in case of conference started)
     */
    eventName?: string;
    /**
     * The JID IM of the user related to the event
     */
    eventJid?: string;
    /**
     * If we can make a call shift for this conversation
     */
    callShift?: boolean;
}
